/**
 * Printora Gang Builder App — Konva editor v2.1
 */
(() => {
  'use strict';

  const GB = () => window.PrintoraGangBuilder;
  const Nest = () => window.PrintoraGangBuilderNest;

  function money(v) {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Local persistence. IndexedDB (large quota, holds full-res images) with a localStorage
   * fallback. Everything is keyed by a scope (customer id or "guest") so different accounts on
   * the same device never see each other's sheets.
   */
  const ProjectStore = (() => {
    const DB_NAME = 'printora_gsb';
    const STORE = 'projects';
    const DRAFTS = 'drafts';
    let dbPromise = null;

    function open() {
      if (dbPromise) return dbPromise;
      dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') { reject(new Error('no idb')); return; }
        const req = indexedDB.open(DB_NAME, 2);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE)) {
            const s = db.createObjectStore(STORE, { keyPath: 'id' });
            s.createIndex('scope', 'scope', { unique: false });
          }
          if (!db.objectStoreNames.contains(DRAFTS)) db.createObjectStore(DRAFTS, { keyPath: 'scope' });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error('blocked'));
      });
      dbPromise.catch(() => { dbPromise = null; });
      return dbPromise;
    }

    function tx(store, mode, fn) {
      return open().then((db) => new Promise((resolve, reject) => {
        const t = db.transaction(store, mode);
        const s = t.objectStore(store);
        let result;
        try { result = fn(s); } catch (e) { reject(e); return; }
        t.oncomplete = () => resolve(result && 'result' in result ? result.result : result);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error || new Error('aborted'));
      }));
    }

    const lsKey = (id) => `printora_gsb_project_${id}`;

    return {
      async put(record) {
        try { await tx(STORE, 'readwrite', (s) => s.put(record)); }
        catch (_) { localStorage.setItem(lsKey(record.id), JSON.stringify(record)); }
      },
      async get(id) {
        try { return await tx(STORE, 'readonly', (s) => s.get(id)); }
        catch (_) { const raw = localStorage.getItem(lsKey(id)); return raw ? JSON.parse(raw) : null; }
      },
      async remove(id) {
        try { await tx(STORE, 'readwrite', (s) => s.delete(id)); }
        catch (_) { localStorage.removeItem(lsKey(id)); }
      },
      async list(scope) {
        let all = [];
        try {
          all = await tx(STORE, 'readonly', (s) => s.index('scope').getAll(scope));
        } catch (_) {
          all = Object.keys(localStorage)
            .filter((k) => k.startsWith('printora_gsb_project_'))
            .map((k) => { try { return JSON.parse(localStorage.getItem(k)); } catch (__) { return null; } })
            .filter((r) => r && r.scope === scope);
        }
        return (all || []).sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
      },
      async putDraft(scope, data) {
        try { await tx(DRAFTS, 'readwrite', (s) => s.put({ scope, data, updatedAt: Date.now() })); }
        catch (_) { localStorage.setItem(`printora_gsb_draft_${scope}`, JSON.stringify(data)); }
      },
      async getDraft(scope) {
        try {
          const rec = await tx(DRAFTS, 'readonly', (s) => s.get(scope));
          if (rec?.data) return rec.data;
        } catch (_) { /* fall through */ }
        const raw = localStorage.getItem(`printora_gsb_draft_${scope}`);
        return raw ? JSON.parse(raw) : null;
      },
      async removeDraft(scope) {
        try { await tx(DRAFTS, 'readwrite', (s) => s.delete(scope)); } catch (_) { /* ignore */ }
        localStorage.removeItem(`printora_gsb_draft_${scope}`);
      },
    };
  })();

  class HistoryStack {
    constructor(limit = 60) {
      this.stack = [];
      this.index = -1;
      this.limit = limit;
    }

    push(state) {
      this.stack = this.stack.slice(0, this.index + 1);
      this.stack.push(JSON.stringify(state));
      if (this.stack.length > this.limit) this.stack.shift();
      this.index = this.stack.length - 1;
    }

    canUndo() { return this.index > 0; }
    canRedo() { return this.index < this.stack.length - 1; }

    undo() {
      if (!this.canUndo()) return null;
      this.index -= 1;
      return JSON.parse(this.stack[this.index]);
    }

    redo() {
      if (!this.canRedo()) return null;
      this.index += 1;
      return JSON.parse(this.stack[this.index]);
    }
  }

  class GangBuilderApp {
    constructor(root) {
      this.root = root;
      this.config = GB().parseConfigFromRoot(root);
      this.assets = new Map();
      this.items = [];
      this.spacingMm = this.config.defaultSpacingMm;
      this.sheetLengthMode = 'auto';
      this.manualLengthMm = this.config.defaultSheetLengthMm;
      this.unit = 'mm';
      this.pxPerMm = 2;
      this.basePxPerMm = 2;
      this.zoom = 1;
      this.fitMode = 'balanced';
      this.history = new HistoryStack();
      this.clipboard = [];
      this.styleClipboard = null;
      this.selectedIds = new Set();
      this.stage = null;
      this.bgLayer = null;
      this.layer = null;
      this.uiLayer = null;
      this.transformer = null;
      this.sheetRect = null;
      this.selectRect = null;
      this.isSelecting = false;
      this.selectStart = null;
      this._onWindowPointerUp = () => this.onStagePointerUp();
      this.imageCache = new Map();
      this.sourceCache = new Map();
      this.dragSnapshot = null;
      this.dragNodeStart = null;
      this.cropStage = null;
      this.cropAssetId = null;
      this.adjustSnapshot = null;
      this.bgEdit = null;
      this.textScale = Number(localStorage.getItem('pgb_text_scale') || 1.3);
      this.darkMode = localStorage.getItem('pgb_dark_mode') === '1';
      this.showOutlines = localStorage.getItem('pgb_show_outlines') !== '0';
      this.snapEdges = localStorage.getItem('pgb_snap_edges') !== '0';
      this.activeRail = 'uploads';
      this.drawerCollapsed = false;
      this.boldOn = true;
      this.italicOn = false;
      this.outlineOn = false;
      this.maskEditId = null;
      this._textEditor = null;
      /* Storage scope: logged-in customer id, otherwise this browser's guest space */
      this.customerId = String(root.dataset.gsbCustomer || '').trim();
      this.storageScope = this.customerId ? `c${this.customerId}` : 'guest';
      this.currentProjectId = null;
      this.currentProjectName = null;
      this._restoring = true;

      this.cacheDom();
      this.applyTextScale();
      this.applyTheme();
      this.initLibraryUi();
      this.initStage();
      this.bindEvents();
      this.setActiveRail('uploads');
      this.pushHistory();
      this.renderAll();
    }

    cacheDom() {
      const q = (s) => this.root.querySelector(s);
      this.fileInput = q('[data-pgb-file]');
      this.upload = q('[data-pgb-upload]');
      this.designList = q('[data-pgb-designs]');
      this.designBadge = q('[data-pgb-design-count]');
      this.errorBox = q('[data-pgb-error]');
      this.lengthEl = q('[data-pgb-length]');
      this.addBtn = q('[data-pgb-add-cart]');
      this.downloadBtn = q('[data-pgb-download]');
      this.nestBtn = q('[data-pgb-nest-all]');
      this.clearBtn = q('[data-pgb-clear]');
      this.undoBtn = q('[data-pgb-undo]');
      this.redoBtn = q('[data-pgb-redo]');
      this.spacingInput = q('[data-pgb-spacing]');
      this.dock = q('[data-pgb-dock]');
      this.dockEmpty = q('[data-pgb-dock-empty]');
      this.dockForm = q('[data-pgb-dock-form]');
      this.dockWidth = q('[data-pgb-width]');
      this.dockHeight = q('[data-pgb-height]');
      this.dockWidthLabel = q('[data-pgb-width-label]');
      this.dockHeightLabel = q('[data-pgb-height-label]');
      this.dockQty = q('[data-pgb-qty]');
      this.dockDpi = q('[data-pgb-dpi]');
      this.dockThumb = q('[data-pgb-dock-thumb]');
      this.dockGlyph = q('[data-pgb-dock-glyph]');
      this.dockQtyWrap = q('[data-pgb-dock-qty-wrap]');
      this.dockImageActions = q('[data-pgb-dock-image-actions]');
      this.dockMaskBtn = q('[data-pgb-dock-mask]');
      this.dockUnmaskBtn = q('[data-pgb-dock-unmask]');
      this.dockRenameBtn = q('[data-pgb-dock-rename]');
      this.projectsList = q('[data-pgb-projects]');
      this.projectFileInput = q('[data-pgb-project-file]');
      this.saveStatus = q('[data-pgb-save-status]');
      this.form = q('[data-pgb-form]');
      this.propsHost = q('[data-pgb-properties]');
      this.variantInput = q('[data-pgb-variant]');
      this.qtyInput = q('[data-pgb-qty-cart]');
      this.stageHost = q('[data-pgb-stage]');
      this.stageScroll = q('[data-pgb-stage-scroll]');
      this.zoomLabel = q('[data-pgb-zoom]');
      this.fitBtn = q('[data-pgb-fit]');
      this.fitSheetBtn = q('[data-pgb-fit-sheet]');
      this.fitBalancedBtn = q('[data-pgb-fit-balanced]');
      this.zoomInBtn = q('[data-pgb-zoom-in]');
      this.zoomOutBtn = q('[data-pgb-zoom-out]');
      this.unitBtns = [...this.root.querySelectorAll('[data-pgb-unit]')];
      this.totalEl = q('[data-pgb-total]');
      this.settingsBtn = q('[data-pgb-settings]');
      this.settingsPanel = q('[data-pgb-settings-panel]');
      this.textScaleSelect = q('[data-pgb-text-scale]');
      this.lengthPresets = q('[data-pgb-length-presets]');
      this.customLengthInput = q('[data-pgb-custom-length]');
      this.autoLengthCheck = q('[data-pgb-auto-length]');
      this.addMeterBtn = q('[data-pgb-add-meter]');
      this.pricingTotal = q('[data-pgb-pricing-total]');
      this.pricingSize = q('[data-pgb-pricing-size]');
      this.pricingUsed = q('[data-pgb-pricing-used]');
      this.pricingBillable = q('[data-pgb-pricing-billable]');
      this.pricingRate = q('[data-pgb-pricing-rate]');
      this.pricingDesigns = q('[data-pgb-pricing-designs]');
      this.pricingEff = q('[data-pgb-pricing-eff]');
      this.tierList = q('[data-pgb-tier-list]');
      this.upsellBox = q('[data-pgb-upsell]');
      this.cropModal = q('[data-pgb-crop-modal]');
      this.cropStageHost = q('[data-pgb-crop-stage]');
      this.adjustModal = q('[data-pgb-adjust-modal]');
      this.adjustPreview = q('[data-pgb-adjust-preview]');
      this.adjustUndoBtn = q('[data-pgb-adjust-undo]');
      this.adjustResetBtn = q('[data-pgb-adjust-reset]');
      this.adjustApplyAllBtn = q('[data-pgb-adjust-apply-all]');
      this.bgModal = q('[data-pgb-bg-modal]');
      this.bgPreview = q('[data-pgb-bg-preview]');
      this.bgEraserStage = q('[data-pgb-bg-eraser-stage]');
      this.bgUndoBtn = q('[data-pgb-bg-undo]');
      this.bgResetBtn = q('[data-pgb-bg-reset]');
      this.bgWhiteInput = q('[data-pgb-bg-white]');
      this.bgBlackInput = q('[data-pgb-bg-black]');
      this.bgFeatherInput = q('[data-pgb-bg-feather]');
      this.bgTolInput = q('[data-pgb-bg-tol]');
      this.bgPickColour = q('[data-pgb-bg-pick-colour]');
      this.bgColTol = q('[data-pgb-bg-col-tol]');
      this.eraserSizeInput = q('[data-pgb-eraser-size]');
      this.eraserHardInput = q('[data-pgb-eraser-hard]');
      this.hueInput = q('[data-pgb-hue]');
      this.satInput = q('[data-pgb-sat]');
      this.brightInput = q('[data-pgb-bright]');
      this.darkModeCheck = q('[data-pgb-dark-mode]');
      this.outlinesCheck = q('[data-pgb-show-outlines]');
      this.snapCheck = q('[data-pgb-snap-edges]');
      this.textInput = q('[data-pgb-text-input]');
      this.fontSelect = q('[data-pgb-font]');
      this.fontSizeInput = q('[data-pgb-font-size]');
      this.textColourInput = q('[data-pgb-text-colour]');
      this.textStrokeInput = q('[data-pgb-text-stroke]');
      this.shapeFillInput = q('[data-pgb-shape-fill]');
      this.shapeStrokeInput = q('[data-pgb-shape-stroke]');
      this.iconColourInput = q('[data-pgb-icon-colour]');
      this.iconSearch = q('[data-pgb-icon-search]');
      this.shapeGrid = q('[data-pgb-shape-grid]');
      this.iconGrid = q('[data-pgb-icon-grid]');
      this.formatBar = q('[data-pgb-format-bar]');
      this.graphicColourInput = q('[data-pgb-graphic-colour]');
      this.graphicColourWrap = q('[data-pgb-graphic-colour-wrap]');
      this.spacingAltInput = q('[data-pgb-spacing-alt]');
      this.drawer = q('[data-pgb-drawer]');
      this.studio = q('[data-pgb-studio]');
    }

    applyTextScale() {
      this.root.style.setProperty('--pgb-text-scale', String(this.textScale));
      if (this.textScaleSelect) this.textScaleSelect.value = String(this.textScale);
    }

    applyTheme() {
      this.root.classList.toggle('pgb-app--dark', this.darkMode);
      if (this.darkModeCheck) this.darkModeCheck.checked = this.darkMode;
      if (this.outlinesCheck) this.outlinesCheck.checked = this.showOutlines;
      if (this.snapCheck) this.snapCheck.checked = this.snapEdges;
    }

    initLibraryUi() {
      const Lib = window.PrintoraGangBuilderLibrary;
      if (!Lib) return;
      Lib.ensureFontsLoaded();
      if (this.fontSelect) {
        this.fontSelect.innerHTML = Lib.FONTS.map((font) => (
          `<option value="${font}" style="font-family:'${font}'">${font}</option>`
        )).join('');
        this.fontSelect.value = 'Montserrat';
      }
      if (this.shapeGrid) {
        this.shapeGrid.innerHTML = Lib.SHAPES.map((shape) => {
          const preview = shape.path
            ? `<svg viewBox="0 0 100 100"><path d="${shape.path}" fill="currentColor"/></svg>`
            : shape.id === 'circle'
              ? '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="42" fill="currentColor"/></svg>'
              : shape.id === 'ellipse'
                ? '<svg viewBox="0 0 100 100"><ellipse cx="50" cy="50" rx="46" ry="30" fill="currentColor"/></svg>'
                : shape.id === 'rounded'
                  ? '<svg viewBox="0 0 100 100"><rect x="8" y="16" width="84" height="68" rx="14" fill="currentColor"/></svg>'
                  : '<svg viewBox="0 0 100 100"><rect x="10" y="10" width="80" height="80" fill="currentColor"/></svg>';
          return `<button type="button" class="pgb-shape-tile" data-pgb-shape="${shape.id}" title="${shape.label}">${preview}</button>`;
        }).join('');
      }
      this.renderIconGrid();
    }

    renderIconGrid(filter = '') {
      const Lib = window.PrintoraGangBuilderLibrary;
      if (!Lib || !this.iconGrid) return;
      const q = String(filter || '').trim().toLowerCase();
      const icons = Lib.ICONS.filter((icon) => !q || icon.id.includes(q) || icon.label.includes(q));
      this.iconGrid.innerHTML = icons.map((icon) => (
        `<button type="button" class="pgb-icon-tile" data-pgb-icon="${icon.id}" title="${icon.label}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${icon.path}"/></svg>
        </button>`
      )).join('');
    }

    sheetHeightMm(items = this.items) {
      const used = GB().usedLengthMm(items, this.config.safeMarginMm);
      const minLen = this.config.defaultSheetLengthMm;
      if (this.sheetLengthMode === 'auto') {
        return Math.max(minLen, used + this.config.safeMarginMm);
      }
      return Math.max(this.manualLengthMm, used + this.config.safeMarginMm);
    }

    ensureSheetFitsItems(items) {
      const used = GB().usedLengthMm(items, this.config.safeMarginMm);
      const needed = Math.max(
        this.config.defaultSheetLengthMm,
        GB().roundMm(used + this.config.safeMarginMm)
      );
      if (this.sheetLengthMode === 'auto') return;
      if (needed <= this.manualLengthMm) return;
      this.manualLengthMm = Math.min(
        this.config.maxSheetLengthMm,
        Math.ceil(needed / this.config.billingIncrementMm) * this.config.billingIncrementMm
      );
      if (this.customLengthInput) this.customLengthInput.value = this.manualLengthMm;
      this.syncLengthPresets();
    }

    syncLengthPresets() {
      if (!this.lengthPresets) return;
      this.lengthPresets.querySelectorAll('.pgb-chip').forEach((chip) => {
        const on = this.sheetLengthMode === 'manual'
          && Number(chip.dataset.pgbLength) === this.manualLengthMm;
        chip.classList.toggle('is-active', on);
      });
    }

    billableLengthMm() {
      const used = GB().usedLengthMm(this.items, this.config.safeMarginMm);
      const len = Math.max(used, this.config.defaultSheetLengthMm);
      return GB().priceGangSheet(len, this.config).billableMm;
    }

    initStage() {
      if (!window.Konva || !this.stageHost) return;
      this.applyFitScale();
      const w = this.stageWidthPx();
      const h = this.stageHeightPx();
      this.stage = new Konva.Stage({ container: this.stageHost, width: w, height: h });
      this.bgLayer = new Konva.Layer();
      this.stage.add(this.bgLayer);
      this.layer = new Konva.Layer();
      this.stage.add(this.layer);
      this.uiLayer = new Konva.Layer();
      this.transformer = new Konva.Transformer({
        rotateEnabled: false,
        keepRatio: true,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        borderStroke: '#ed168c',
        borderStrokeWidth: 2,
        anchorStroke: '#ed168c',
        anchorFill: '#fff',
        anchorSize: window.matchMedia?.('(pointer: coarse)')?.matches ? 20 : 12,
        anchorCornerRadius: 2,
        anchorStrokeWidth: 1.5,
        ignoreStroke: true,
        boundBoxFunc: (oldBox, newBox) => {
          const minPx = Math.max(24, this.pxPerMm * this.zoom * 10);
          if (newBox.width < minPx || newBox.height < minPx) return oldBox;
          return newBox;
        },
      });
      this.uiLayer.add(this.transformer);
      this.stage.add(this.uiLayer);
      this.redrawSheetBackground();
      this.stage.on('mousedown touchstart', (e) => this.onStagePointerDown(e));
      this.stage.on('mousemove touchmove', (e) => this.onStagePointerMove(e));
      this.stage.on('mouseup touchend', () => this.onStagePointerUp());
      window.addEventListener('mouseup', this._onWindowPointerUp);
      window.addEventListener('touchend', this._onWindowPointerUp);
      this.transformer.on('transformend', () => this.onTransformEnd());
    }

    stageWidthPx() {
      const sheetW = this.config.sheetWidthMm * this.pxPerMm * this.zoom;
      return Math.ceil(sheetW + 48);
    }

    stageHeightPx() {
      return Math.ceil(this.sheetHeightMm() * this.pxPerMm * this.zoom + 40);
    }

    updateZoomLabel() {
      if (this.zoomLabel) {
        this.zoomLabel.textContent = `${Math.round((this.pxPerMm / this.basePxPerMm) * 100)}%`;
      }
    }

    fitWidthScale() {
      if (!this.stageScroll) return;
      const avail = Math.max(200, this.stageScroll.clientWidth - 8);
      const padding = 48;
      this.pxPerMm = Math.max(0.25, (avail - padding) / this.config.sheetWidthMm);
      this.zoom = 1;
      this.fitMode = 'width';
      this.updateZoomLabel();
      this.updateFitButtons('width');
    }

    fitBalancedScale() {
      if (!this.stageScroll) return;
      const availW = Math.max(240, this.stageScroll.clientWidth - 8);
      const availH = Math.max(320, this.stageScroll.clientHeight - 8);
      const padW = 48;
      const padH = 40;
      const sheetW = this.config.sheetWidthMm;
      const used = GB().usedLengthMm(this.items, this.config.safeMarginMm || 0);
      const targetH = Math.min(
        this.sheetHeightMm(),
        Math.max(450, used * 1.35 + this.spacingMm * 2 + 50)
      );
      const byW = (availW - padW) / sheetW;
      const byH = (availH - padH) / targetH;
      this.pxPerMm = Math.max(1.0, Math.min(byW, byH, 2.6));
      this.zoom = 1;
      this.fitMode = 'balanced';
      this.updateZoomLabel();
      this.updateFitButtons('balanced');
    }

    fitSheetScale() {
      if (!this.stageScroll) return;
      const availW = Math.max(200, this.stageScroll.clientWidth - 8);
      const availH = Math.max(160, this.stageScroll.clientHeight - 8);
      const padW = 48;
      const padH = 40;
      const sheetW = this.config.sheetWidthMm;
      const sheetH = Math.max(this.sheetHeightMm(), 100);
      const byW = (availW - padW) / sheetW;
      const byH = (availH - padH) / sheetH;
      this.pxPerMm = Math.max(0.35, Math.min(byW, byH));
      this.zoom = 1;
      this.fitMode = 'sheet';
      this.updateZoomLabel();
      this.updateFitButtons('sheet');
    }

    updateFitButtons(active) {
      const map = { balanced: '[data-pgb-fit-balanced]', width: '[data-pgb-fit]', sheet: '[data-pgb-fit-sheet]' };
      this.root.querySelectorAll('[data-pgb-fit-balanced],[data-pgb-fit],[data-pgb-fit-sheet]').forEach((b) => {
        b.classList.remove('is-active');
      });
      const sel = map[active];
      if (sel) this.root.querySelectorAll(sel).forEach((b) => b.classList.add('is-active'));
    }

    applyFitScale() {
      if (this.fitMode === 'width') this.fitWidthScale();
      else if (this.fitMode === 'sheet') this.fitSheetScale();
      else this.fitBalancedScale();
    }

    setZoomLevel(percent) {
      this.pxPerMm = Math.max(0.2, Math.min(5, (percent / 100) * this.basePxPerMm));
      this.zoom = 1;
      this.fitMode = 'custom';
      this.updateZoomLabel();
    }

    zoomBy(delta, anchor = null) {
      const current = Math.round((this.pxPerMm / this.basePxPerMm) * 100);
      const scroll = this.stageScroll;
      const host = this.stageHost;
      let anchorMm = null;
      let anchorPx = null;
      if (scroll && host) {
        const rect = scroll.getBoundingClientRect();
        const cx = anchor ? anchor.clientX - rect.left : rect.width / 2;
        const cy = anchor ? anchor.clientY - rect.top : rect.height / 2;
        anchorPx = { x: cx, y: cy };
        anchorMm = {
          x: this.stageToMmX(cx + scroll.scrollLeft - host.offsetLeft),
          y: this.stageToMmY(cy + scroll.scrollTop - host.offsetTop),
        };
      }
      this.setZoomLevel(current + delta);
      this.renderAll();
      if (scroll && host && anchorMm) {
        scroll.scrollLeft = Math.max(0, host.offsetLeft + this.mmToStageX(anchorMm.x) - anchorPx.x);
        scroll.scrollTop = Math.max(0, host.offsetTop + this.mmToStageY(anchorMm.y) - anchorPx.y);
      }
    }

    /* Two-finger pinch on the canvas zooms around the midpoint (phones / iPads). */
    bindPinchZoom() {
      const scroll = this.stageScroll;
      if (!scroll) return;
      let pinch = null;
      const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
      const mid = (t) => ({ clientX: (t[0].clientX + t[1].clientX) / 2, clientY: (t[0].clientY + t[1].clientY) / 2 });
      scroll.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          pinch = { startDist: dist(e.touches), startZoom: Math.round((this.pxPerMm / this.basePxPerMm) * 100), last: 0 };
          /* Stop Konva from treating the second finger as a drag */
          if (this.stage) this.stage.stopDrag?.();
          e.preventDefault();
        }
      }, { passive: false });
      scroll.addEventListener('touchmove', (e) => {
        if (!pinch || e.touches.length !== 2) return;
        e.preventDefault();
        const ratio = dist(e.touches) / pinch.startDist;
        const target = Math.round(pinch.startZoom * ratio / 5) * 5;
        const current = Math.round((this.pxPerMm / this.basePxPerMm) * 100);
        if (target !== current && Math.abs(target - current) >= 5) {
          this.zoomBy(target - current, mid(e.touches));
        }
      }, { passive: false });
      const end = () => { pinch = null; };
      scroll.addEventListener('touchend', end);
      scroll.addEventListener('touchcancel', end);
    }

    /* Hover tooltips: every [title] inside the builder becomes a quick, styled tooltip. */
    initTooltips() {
      if (this._tooltipEl) return;
      const tip = document.createElement('div');
      tip.className = 'pgb-tooltip';
      tip.hidden = true;
      tip.setAttribute('role', 'tooltip');
      document.body.appendChild(tip);
      this._tooltipEl = tip;

      const convert = (el) => {
        if (!(el instanceof Element)) return;
        if (el.hasAttribute('title')) {
          const t = el.getAttribute('title');
          if (t) el.dataset.pgbTip = t;
          el.removeAttribute('title');
        }
        el.querySelectorAll?.('[title]').forEach((child) => {
          const t = child.getAttribute('title');
          if (t) child.dataset.pgbTip = t;
          child.removeAttribute('title');
        });
      };
      convert(this.root);
      const mo = new MutationObserver((muts) => {
        muts.forEach((m) => {
          if (m.type === 'attributes') convert(m.target);
          m.addedNodes?.forEach(convert);
        });
      });
      mo.observe(this.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['title'] });

      let current = null;
      let timer = null;
      const hide = () => {
        clearTimeout(timer);
        timer = null;
        current = null;
        tip.classList.remove('is-visible');
        tip.hidden = true;
      };
      const show = (el) => {
        const text = el.dataset.pgbTip;
        if (!text || !el.isConnected) return;
        tip.textContent = text;
        tip.hidden = false;
        const r = el.getBoundingClientRect();
        const tw = tip.offsetWidth;
        const th = tip.offsetHeight;
        let x = r.left + r.width / 2 - tw / 2;
        x = Math.max(8, Math.min(window.innerWidth - tw - 8, x));
        let y = r.bottom + 8;
        if (y + th > window.innerHeight - 8) y = r.top - th - 8;
        tip.style.left = `${Math.round(x)}px`;
        tip.style.top = `${Math.round(y)}px`;
        tip.classList.add('is-visible');
      };
      const schedule = (el) => {
        if (el === current) return;
        clearTimeout(timer);
        current = el;
        tip.classList.remove('is-visible');
        tip.hidden = true;
        timer = setTimeout(() => show(el), 320);
      };
      this.root.addEventListener('pointerover', (e) => {
        const el = e.target.closest?.('[data-pgb-tip]');
        if (!el) { if (current) hide(); return; }
        schedule(el);
      });
      this.root.addEventListener('pointerout', (e) => {
        const el = e.target.closest?.('[data-pgb-tip]');
        if (el && el === current && !(e.relatedTarget && el.contains(e.relatedTarget))) hide();
      });
      this.root.addEventListener('pointerdown', hide, true);
      this.root.addEventListener('focusin', (e) => {
        const el = e.target.closest?.('[data-pgb-tip]');
        if (el && e.target.matches(':focus-visible')) schedule(el);
      });
      this.root.addEventListener('focusout', hide);
      window.addEventListener('scroll', hide, true);
    }

    mmToStageX(mm) { return 24 + mm * this.pxPerMm * this.zoom; }
    mmToStageY(mm) { return 20 + mm * this.pxPerMm * this.zoom; }
    stageToMmX(px) { return (px - 24) / (this.pxPerMm * this.zoom); }
    stageToMmY(px) { return (px - 20) / (this.pxPerMm * this.zoom); }

    checkerPattern() {
      if (this._checker) return this._checker;
      const c = document.createElement('canvas');
      c.width = 20;
      c.height = 20;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#e8e8ea';
      ctx.fillRect(0, 0, 20, 20);
      ctx.fillStyle = '#d4d4d8';
      ctx.fillRect(10, 0, 10, 10);
      ctx.fillRect(0, 10, 10, 10);
      this._checker = c;
      return c;
    }

    redrawSheetBackground() {
      if (!this.bgLayer) return;
      const w = this.config.sheetWidthMm * this.pxPerMm * this.zoom;
      const h = this.sheetHeightMm() * this.pxPerMm * this.zoom;
      const ox = 24;
      const oy = 20;
      if (this.stage) {
        const sw = this.stageWidthPx();
        const sh = this.stageHeightPx();
        if (this.stage.width() !== sw) this.stage.width(sw);
        if (this.stage.height() !== sh) this.stage.height(sh);
      }
      /* Single pattern-filled rect instead of thousands of tiny squares: keeps dragging smooth */
      if (!this.sheetBg || !this.sheetRect || this.sheetBg.getLayer() !== this.bgLayer) {
        this.bgLayer.destroyChildren();
        this.sheetBg = new Konva.Rect({
          x: ox, y: oy, width: w, height: h,
          fillPatternImage: this.checkerPattern(),
          fillPatternRepeat: 'repeat',
          listening: false,
          perfectDrawEnabled: false,
        });
        this.sheetRect = new Konva.Rect({
          x: ox, y: oy, width: w, height: h,
          stroke: '#ed168c', strokeWidth: 2, fill: 'transparent', listening: false,
          perfectDrawEnabled: false,
        });
        this.bgLayer.add(this.sheetBg);
        this.bgLayer.add(this.sheetRect);
      } else {
        this.sheetBg.size({ width: w, height: h });
        this.sheetRect.size({ width: w, height: h });
      }
      this.bgLayer.batchDraw();
    }

    bindEvents() {
      this.fileInput?.addEventListener('change', (e) => {
        this.handleFiles(e.target.files);
        e.target.value = '';
      });
      ['dragenter', 'dragover'].forEach((t) => {
        this.upload?.addEventListener(t, (e) => { e.preventDefault(); e.stopPropagation(); this.upload.classList.add('is-dragover'); });
      });
      ['dragleave', 'drop'].forEach((t) => {
        this.upload?.addEventListener(t, (e) => { e.preventDefault(); e.stopPropagation(); this.upload.classList.remove('is-dragover'); });
      });
      this.upload?.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.upload.classList.remove('is-dragover');
        this.handleFiles(e.dataTransfer?.files);
      });

      const canvasDrop = this.stageScroll || this.stageHost;
      ['dragenter', 'dragover'].forEach((t) => {
        canvasDrop?.addEventListener(t, (e) => {
          if (![...e.dataTransfer?.types || []].includes('Files')) return;
          e.preventDefault();
          e.stopPropagation();
          canvasDrop.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach((t) => {
        canvasDrop?.addEventListener(t, (e) => {
          if (t === 'drop' && ![...e.dataTransfer?.types || []].includes('Files')) return;
          if (t === 'dragleave' && canvasDrop.contains(e.relatedTarget)) return;
          e.preventDefault();
          e.stopPropagation();
          canvasDrop.classList.remove('is-dragover');
          if (t === 'drop') this.handleFiles(e.dataTransfer?.files);
        });
      });

      this.root.querySelectorAll('button[data-pgb-rail]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.setActiveRail(btn.dataset.pgbRail);
        });
      });
      this.root.querySelector('[data-pgb-drawer-collapse]')?.addEventListener('click', () => {
        this.drawerCollapsed = !this.drawerCollapsed;
        this.root.classList.toggle('is-drawer-collapsed', this.drawerCollapsed);
      });

      this.root.querySelectorAll('[data-pgb-nest-all]').forEach((btn) => {
        btn.addEventListener('click', () => this.nestAll());
      });
      this.clearBtn?.addEventListener('click', () => {
        if (!this.items.length || window.confirm('Clear entire gang sheet? Saved gang sheets are kept.')) {
          this.items = [];
          this.assets.clear();
          this.imageCache.clear();
          this.sourceCache.clear();
          this.selectedIds.clear();
          this.currentProjectId = null;
          this.currentProjectName = null;
          this.pushHistory();
          this.renderAll();
          this.renderProjectsList();
        }
      });
      this.undoBtn?.addEventListener('click', () => this.undo());
      this.redoBtn?.addEventListener('click', () => this.redo());
      this.downloadBtn?.addEventListener('click', () => this.downloadExport());
      this.addBtn?.addEventListener('click', () => this.addToCart());
      this.root.querySelectorAll('[data-pgb-fit]').forEach((btn) => {
        btn.addEventListener('click', () => { this.fitWidthScale(); this.renderAll(); });
      });
      this.root.querySelectorAll('[data-pgb-fit-balanced]').forEach((btn) => {
        btn.addEventListener('click', () => { this.fitBalancedScale(); this.renderAll(); });
      });
      this.root.querySelectorAll('[data-pgb-fit-sheet]').forEach((btn) => {
        btn.addEventListener('click', () => { this.fitSheetScale(); this.renderAll(); });
      });
      this.zoomInBtn?.addEventListener('click', () => this.zoomBy(15));
      this.zoomOutBtn?.addEventListener('click', () => this.zoomBy(-15));
      this.stageScroll?.addEventListener('wheel', (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        this.zoomBy(e.deltaY > 0 ? -10 : 10, { clientX: e.clientX, clientY: e.clientY });
      }, { passive: false });
      this.bindPinchZoom();
      this.initTooltips();
      const onSpacing = (input) => {
        this.spacingMm = Math.max(0, Math.min(20, Number(input.value) || 3));
        if (this.spacingInput) this.spacingInput.value = this.spacingMm;
        if (this.spacingAltInput) this.spacingAltInput.value = this.spacingMm;
        this.nestAll();
      };
      this.spacingInput?.addEventListener('change', () => onSpacing(this.spacingInput));
      this.spacingAltInput?.addEventListener('change', () => onSpacing(this.spacingAltInput));

      this.settingsBtn?.addEventListener('click', () => {
        if (this.settingsPanel) this.settingsPanel.hidden = !this.settingsPanel.hidden;
      });
      this.root.querySelector('[data-pgb-settings-close]')?.addEventListener('click', () => {
        if (this.settingsPanel) this.settingsPanel.hidden = true;
      });
      this.textScaleSelect?.addEventListener('change', () => {
        this.textScale = Number(this.textScaleSelect.value) || 1.3;
        localStorage.setItem('pgb_text_scale', String(this.textScale));
        this.applyTextScale();
      });
      this.darkModeCheck?.addEventListener('change', () => {
        this.darkMode = Boolean(this.darkModeCheck.checked);
        localStorage.setItem('pgb_dark_mode', this.darkMode ? '1' : '0');
        this.applyTheme();
      });
      this.outlinesCheck?.addEventListener('change', () => {
        this.showOutlines = Boolean(this.outlinesCheck.checked);
        localStorage.setItem('pgb_show_outlines', this.showOutlines ? '1' : '0');
        this.renderKonvaItems();
      });
      this.snapCheck?.addEventListener('change', () => {
        this.snapEdges = Boolean(this.snapCheck.checked);
        localStorage.setItem('pgb_snap_edges', this.snapEdges ? '1' : '0');
      });

      this.root.querySelector('[data-pgb-add-text]')?.addEventListener('click', () => this.addTextItem());
      this.root.querySelectorAll('[data-pgb-text-preset]').forEach((btn) => {
        btn.addEventListener('click', () => this.addTextPreset(btn.dataset.pgbTextPreset));
      });
      this.shapeGrid?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pgb-shape]');
        if (btn) this.addShapeItem(btn.dataset.pgbShape);
      });
      this.iconGrid?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pgb-icon]');
        if (btn) this.addIconItem(btn.dataset.pgbIcon);
      });
      this.iconSearch?.addEventListener('input', () => this.renderIconGrid(this.iconSearch.value));
      this.root.querySelector('[data-pgb-mask-selected]')?.addEventListener('click', () => this.maskSelectionToShape());
      this.root.querySelector('[data-pgb-clear-mask]')?.addEventListener('click', () => this.clearMaskOnSelection());

      this.textColourInput?.addEventListener('input', () => this.updateSelectedGraphicStyle());
      this.textStrokeInput?.addEventListener('input', () => this.updateSelectedGraphicStyle());
      this.fontSelect?.addEventListener('change', () => this.updateSelectedGraphicStyle());
      this.fontSizeInput?.addEventListener('change', () => this.updateSelectedGraphicStyle());
      this.shapeFillInput?.addEventListener('input', () => this.updateSelectedGraphicStyle());
      this.shapeStrokeInput?.addEventListener('input', () => this.updateSelectedGraphicStyle());
      this.root.querySelector('[data-pgb-shape-fill-none]')?.addEventListener('click', () => {
        this.getSelectedItems().filter((i) => i.kind === 'shape').forEach((i) => { i.fill = 'transparent'; });
        this.pushHistory();
        this.renderKonvaItems();
        this.syncFormatBar();
      });
      this.iconColourInput?.addEventListener('input', () => this.updateSelectedGraphicStyle());
      this.graphicColourInput?.addEventListener('input', () => {
        const colour = this.graphicColourInput.value;
        if (this.shapeFillInput) this.shapeFillInput.value = colour;
        if (this.iconColourInput) this.iconColourInput.value = colour;
        this.getSelectedItems().forEach((item) => {
          if (item.kind === 'shape') item.fill = colour;
          if (item.kind === 'icon') item.stroke = colour;
        });
        this.updateSelectedGraphicStyle();
      });
      this.root.querySelector('[data-pgb-font-smaller]')?.addEventListener('click', () => {
        if (!this.fontSizeInput) return;
        this.fontSizeInput.value = Math.max(12, Number(this.fontSizeInput.value || 48) - 4);
        this.updateSelectedGraphicStyle();
      });
      this.root.querySelector('[data-pgb-font-larger]')?.addEventListener('click', () => {
        if (!this.fontSizeInput) return;
        this.fontSizeInput.value = Math.min(400, Number(this.fontSizeInput.value || 48) + 4);
        this.updateSelectedGraphicStyle();
      });
      this.root.querySelector('[data-pgb-bold]')?.addEventListener('click', (e) => {
        this.boldOn = !this.boldOn;
        e.currentTarget.classList.toggle('is-active', this.boldOn);
        this.updateSelectedGraphicStyle();
      });
      this.root.querySelector('[data-pgb-italic]')?.addEventListener('click', (e) => {
        this.italicOn = !this.italicOn;
        e.currentTarget.classList.toggle('is-active', this.italicOn);
        this.updateSelectedGraphicStyle();
      });
      this.root.querySelector('[data-pgb-outline-toggle]')?.addEventListener('click', (e) => {
        this.outlineOn = !this.outlineOn;
        e.currentTarget.classList.toggle('is-active', this.outlineOn);
        this.getSelectedItems().filter((i) => i.kind === 'text').forEach((item) => {
          item.strokeWidth = this.outlineOn ? Math.max(1, item.strokeWidth || 2) : 0;
          if (this.outlineOn && !item.stroke) item.stroke = this.textStrokeInput?.value || '#111111';
        });
        this.pushHistory();
        this.renderKonvaItems();
        this.syncFormatBar();
      });
      this.textInput?.addEventListener('change', () => this.updateSelectedGraphicStyle());
      this.textColourInput?.addEventListener('input', () => {
        const chip = this.root.querySelector('[data-pgb-text-colour-chip]');
        if (chip) chip.style.background = this.textColourInput.value;
        this.updateSelectedGraphicStyle();
      });
      this.textStrokeInput?.addEventListener('input', () => {
        const chip = this.root.querySelector('[data-pgb-text-stroke-chip]');
        if (chip) chip.style.background = this.textStrokeInput.value;
        if (!this.outlineOn) {
          this.outlineOn = true;
          this.root.querySelector('[data-pgb-outline-toggle]')?.classList.add('is-active');
        }
        this.getSelectedItems().filter((i) => i.kind === 'text').forEach((item) => {
          item.stroke = this.textStrokeInput.value;
          item.strokeWidth = Math.max(1, item.strokeWidth || 2);
        });
        this.pushHistory();
        this.renderKonvaItems();
        this.syncFormatBar();
      });

      this.lengthPresets?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pgb-length]');
        if (!btn) return;
        this.manualLengthMm = Number(btn.dataset.pgbLength);
        this.sheetLengthMode = 'manual';
        if (this.autoLengthCheck) this.autoLengthCheck.checked = false;
        if (this.customLengthInput) this.customLengthInput.value = this.manualLengthMm;
        this.lengthPresets.querySelectorAll('.pgb-chip').forEach((c) => c.classList.toggle('is-active', c === btn));
        this.renderAll();
      });

      this.addMeterBtn?.addEventListener('click', () => {
        this.manualLengthMm = (this.sheetLengthMode === 'auto' ? this.sheetHeightMm() : this.manualLengthMm) + 1000;
        this.sheetLengthMode = 'manual';
        if (this.autoLengthCheck) this.autoLengthCheck.checked = false;
        if (this.customLengthInput) this.customLengthInput.value = this.manualLengthMm;
        this.syncLengthPresets();
        this.renderAll();
      });

      this.customLengthInput?.addEventListener('change', () => {
        this.manualLengthMm = Math.max(1000, Number(this.customLengthInput.value) || 1000);
        this.sheetLengthMode = 'manual';
        if (this.autoLengthCheck) this.autoLengthCheck.checked = false;
        this.renderAll();
      });

      this.autoLengthCheck?.addEventListener('change', () => {
        this.sheetLengthMode = this.autoLengthCheck.checked ? 'auto' : 'manual';
        this.syncLengthPresets();
        this.renderAll();
      });

      this.designList?.addEventListener('dblclick', (e) => {
        if (e.target.closest('input')) return;
        const row = e.target.closest('[data-pgb-asset-id], [data-pgb-instance-id]');
        if (!row || !e.target.closest('.pgb-design__meta')) return;
        e.preventDefault();
        this.startRename(row.dataset.pgbAssetId || row.dataset.pgbInstanceId);
      });
      this.designList?.addEventListener('click', (e) => {
        if (e.target.closest('input.pgb-rename-input')) return;
        const renameBtn = e.target.closest('[data-pgb-rename]');
        if (renameBtn) {
          e.preventDefault();
          e.stopPropagation();
          const row = renameBtn.closest('[data-pgb-asset-id], [data-pgb-instance-id]');
          if (row) this.startRename(row.dataset.pgbAssetId || row.dataset.pgbInstanceId);
          return;
        }
        const instanceRow = e.target.closest('[data-pgb-instance-id]');
        if (instanceRow) {
          if (e.target.closest('[data-pgb-remove-instance]')) {
            e.preventDefault();
            e.stopPropagation();
            const id = instanceRow.dataset.pgbInstanceId;
            this.items = this.items.filter((i) => i.instanceId !== id);
            this.selectedIds.delete(id);
            this.pushHistory();
            this.renderAll();
            return;
          }
          const id = instanceRow.dataset.pgbInstanceId;
          if (!e.shiftKey) this.selectedIds.clear();
          this.selectedIds.add(id);
          this.syncKonvaSelection();
          this.syncDock();
          this.renderDesignList();
          return;
        }
        const row = e.target.closest('[data-pgb-asset-id]');
        if (!row) return;
        if (e.target.closest('[data-pgb-remove]')) {
          e.preventDefault();
          e.stopPropagation();
          this.removeAsset(row.dataset.pgbAssetId);
          return;
        }
        this.selectInstancesByAsset(row.dataset.pgbAssetId, e.shiftKey);
      });

      this.dockWidth?.addEventListener('change', () => this.applyDockSize(false));
      this.dockHeight?.addEventListener('change', () => this.applyDockSize(true));
      this.dockQty?.addEventListener('change', () => this.applyDockQty());
      this.root.querySelector('[data-pgb-qty-minus]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.dockQty || this.dockForm?.hidden) return;
        this.dockQty.value = Math.max(1, Number(this.dockQty.value) - 1);
        this.applyDockQty();
      });
      this.root.querySelector('[data-pgb-qty-plus]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.dockQty || this.dockForm?.hidden) return;
        this.dockQty.value = Number(this.dockQty.value) + 1;
        this.applyDockQty();
      });

      this.root.querySelector('[data-pgb-rotate-cw]')?.addEventListener('click', () => this.rotateSelected(90));
      this.root.querySelector('[data-pgb-duplicate]')?.addEventListener('click', () => this.duplicateSelected());
      this.root.querySelector('[data-pgb-delete]')?.addEventListener('click', () => this.deleteSelected());
      this.root.querySelector('[data-pgb-crop]')?.addEventListener('click', () => this.openCropModal());
      this.root.querySelector('[data-pgb-remove-bg]')?.addEventListener('click', () => this.quickRemoveBackground());
      this.root.querySelector('[data-pgb-bg-advanced]')?.addEventListener('click', () => this.openBgModal());
      this.root.querySelector('[data-pgb-copy-style]')?.addEventListener('click', () => this.copyStyle());
      this.root.querySelector('[data-pgb-paste-style]')?.addEventListener('click', () => this.pasteStyle());
      this.dockMaskBtn?.addEventListener('click', () => this.maskSelectionToShape());
      this.dockUnmaskBtn?.addEventListener('click', () => this.clearMaskOnSelection());
      this.dockRenameBtn?.addEventListener('click', () => {
        const primary = this.getSelectedItems()[0];
        if (primary) this.startRename(primary.kind && primary.kind !== 'image' ? primary.instanceId : primary.id);
      });
      this.root.querySelector('[data-pgb-project-save]')?.addEventListener('click', () => this.saveProjectToLibrary());
      this.root.querySelector('[data-pgb-project-download]')?.addEventListener('click', () => this.downloadProjectFile());
      this.root.querySelector('[data-pgb-project-open]')?.addEventListener('click', () => this.projectFileInput?.click());
      this.projectFileInput?.addEventListener('change', (e) => {
        const file = e.target.files?.[0];
        if (file) this.loadProjectFile(file);
        e.target.value = '';
      });
      this.projectsList?.addEventListener('click', (e) => {
        const loadBtn = e.target.closest('[data-pgb-project-load]');
        const delBtn = e.target.closest('[data-pgb-project-delete]');
        const dlBtn = e.target.closest('[data-pgb-project-export]');
        const id = (loadBtn || delBtn || dlBtn)?.closest('[data-pgb-project-id]')?.dataset.pgbProjectId;
        if (!id) return;
        e.preventDefault();
        if (loadBtn) this.loadProjectFromLibrary(id);
        else if (delBtn) this.deleteProjectFromLibrary(id);
        else if (dlBtn) this.downloadProjectFile(id);
      });
      this.root.querySelector('[data-pgb-adjust]')?.addEventListener('click', () => this.openAdjustModal());

      this.root.querySelectorAll('[data-pgb-crop-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeCropModal());
      });
      this.root.querySelector('[data-pgb-crop-apply]')?.addEventListener('click', () => this.applyCrop());

      this.root.querySelectorAll('[data-pgb-adjust-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeAdjustModal());
      });
      this.root.querySelector('[data-pgb-adjust-apply]')?.addEventListener('click', () => this.applyAdjust(false));
      this.adjustApplyAllBtn?.addEventListener('click', () => this.applyAdjust(true));
      this.adjustUndoBtn?.addEventListener('click', () => this.undoAdjustPreview());
      this.adjustResetBtn?.addEventListener('click', () => this.resetAdjustPreview());
      [this.hueInput, this.satInput, this.brightInput].forEach((inp) => {
        inp?.addEventListener('input', () => {
          this.pushAdjustPreview();
          this.previewAdjust();
        });
      });

      this.root.querySelectorAll('[data-pgb-bg-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeBgModal());
      });
      this.root.querySelector('[data-pgb-bg-apply]')?.addEventListener('click', () => this.applyBgEdit());
      this.bgUndoBtn?.addEventListener('click', () => this.undoBgEdit());
      this.bgResetBtn?.addEventListener('click', () => this.resetBgEdit());
      this.root.querySelector('[data-pgb-bg-apply-color]')?.addEventListener('click', () => this.applyBgColorRemove());
      this.root.querySelector('[data-pgb-bg-auto-run]')?.addEventListener('click', () => this.runBgAutoPreview());
      this.root.querySelectorAll('[data-pgb-bg-tab]').forEach((btn) => {
        btn.addEventListener('click', () => this.setBgTab(btn.dataset.pgbBgTab));
      });
      [this.bgWhiteInput, this.bgBlackInput, this.bgFeatherInput, this.bgTolInput].forEach((inp) => {
        inp?.addEventListener('input', () => this.runBgAutoPreview());
      });
      [this.eraserSizeInput, this.eraserHardInput].forEach((inp) => {
        inp?.addEventListener('input', () => {
          if (this.bgEdit?.eraserStage) this.initBgEraserStage();
        });
      });

      this.unitBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.unit = btn.dataset.pgbUnit === 'in' ? 'in' : 'mm';
          this.unitBtns.forEach((b) => b.classList.toggle('is-active', b === btn));
          this.syncDock();
          this.renderDesignList();
        });
      });

      this.root.addEventListener('keydown', (e) => this.onKeyDown(e));
      document.addEventListener('keydown', (e) => {
        if (!this.root.isConnected) return;
        if (e.target?.closest?.('[data-pgb-root]') === this.root) return;
        this.onKeyDown(e);
      });
      /* Only re-fit when the browser window itself is resized — never because of UI/content changes */
      let resizeTimer = null;
      let lastWin = { w: window.innerWidth, h: window.innerHeight };
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (Math.abs(window.innerWidth - lastWin.w) < 2 && Math.abs(window.innerHeight - lastWin.h) < 2) return;
          lastWin = { w: window.innerWidth, h: window.innerHeight };
          if (this.fitMode !== 'custom') this.applyFitScale();
          this.renderKonvaItems();
        }, 120);
      });
      setInterval(() => this.autosave(), 8000);
    }

    onKeyDown(e) {
      /* Typing in a field (rename, sizes, text entry…) must never trigger canvas shortcuts */
      const active = document.activeElement;
      if (active && e.key !== 'Escape' && (active.matches('input, textarea, select') || active.isContentEditable)) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'z' && !e.shiftKey) { e.preventDefault(); this.undo(); }
      if ((mod && e.key === 'z' && e.shiftKey) || (mod && e.key === 'y')) { e.preventDefault(); this.redo(); }
      if (mod && e.key === 'c') { e.preventDefault(); this.copySelected(); }
      if (mod && e.key === 'v') { e.preventDefault(); this.pasteClipboard(); }
      if (mod && e.key === 'd') { e.preventDefault(); this.duplicateSelected(); }
      if (mod && e.key === 'a') {
        e.preventDefault();
        this.items.forEach((i) => this.selectedIds.add(i.instanceId));
        this.syncKonvaSelection();
        this.syncDock();
      }
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        if (document.activeElement?.matches('input, textarea, select')) return;
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        this.nudgeSelected(
          e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0,
          e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
        );
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (document.activeElement?.matches('input, textarea, [contenteditable="true"]')) return;
        if (!this.selectedIds.size) return;
        e.preventDefault();
        this.deleteSelected();
      }
      if (e.key === 'Escape') {
        if (this.cropModal && !this.cropModal.hidden) this.closeCropModal();
        else if (this.adjustModal && !this.adjustModal.hidden) this.closeAdjustModal();
        else if (this.bgModal && !this.bgModal.hidden) this.closeBgModal();
        else this.clearSelection();
      }
    }

    getState() {
      return {
        items: this.items.map((i) => ({ ...i })),
        assets: [...this.assets.values()],
        spacingMm: this.spacingMm,
        sheetLengthMode: this.sheetLengthMode,
        manualLengthMm: this.manualLengthMm,
      };
    }

    applyState(state) {
      this.items = (state.items || []).map((i) => ({ ...i }));
      this.spacingMm = state.spacingMm ?? this.spacingMm;
      if (this.spacingInput) this.spacingInput.value = this.spacingMm;
      this.sheetLengthMode = state.sheetLengthMode || 'auto';
      this.manualLengthMm = state.manualLengthMm || this.config.defaultSheetLengthMm;
      if (this.autoLengthCheck) this.autoLengthCheck.checked = this.sheetLengthMode === 'auto';
      (state.assets || []).forEach((a) => {
        this.assets.set(a.id, { ...a, allowAutoRotate: true });
        const img = new Image();
        img.src = a.imageUrl;
        this.imageCache.set(a.id, img);
        if (!a.thumbUrl && a.imageUrl) {
          img.onload = () => {
            if (!this.assets.get(a.id)?.thumbUrl) {
              const asset = this.assets.get(a.id);
              if (asset) asset.thumbUrl = this.createAssetThumb(img);
              this.renderDesignList();
            }
          };
        }
      });
      this.selectedIds.clear();
      this.renderAll();
    }

    pushHistory() {
      this.history.push(this.getState());
      this.updateUndoButtons();
      this.markSaved();
      clearTimeout(this._autosaveTimer);
      this._autosaveTimer = setTimeout(() => this.autosave(), 600);
    }

    undo() { const s = this.history.undo(); if (s) this.applyState(s); this.updateUndoButtons(); }
    redo() { const s = this.history.redo(); if (s) this.applyState(s); this.updateUndoButtons(); }
    updateUndoButtons() {
      if (this.undoBtn) this.undoBtn.disabled = !this.history.canUndo();
      if (this.redoBtn) this.redoBtn.disabled = !this.history.canRedo();
    }
    markSaved() { if (this.saveStatus) this.saveStatus.textContent = 'Saved ✓'; }

    createAssetThumb(img, size = 56) {
      const w = img.naturalWidth || img.width || 1;
      const h = img.naturalHeight || img.height || 1;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, size, size);
      const scale = Math.min(size / w, size / h);
      const dw = w * scale;
      const dh = h * scale;
      ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
      return canvas.toDataURL('image/jpeg', 0.82);
    }

    autosave() {
      if (this._restoring) return;
      const data = {
        state: this.getState(),
        projectId: this.currentProjectId,
        projectName: this.currentProjectName,
        savedAt: Date.now(),
      };
      ProjectStore.putDraft(this.storageScope, data).then(() => this.markSaved()).catch(() => {});
    }

    async processImageFile(file) {
      try {
        const url = URL.createObjectURL(file);
        const img = await new Promise((res, rej) => {
          const i = new Image();
          i.onload = () => res(i);
          i.onerror = () => rej(new Error(`Could not read ${file.name}`));
          i.src = url;
        });
        URL.revokeObjectURL(url);
        let finalImg = img;
        try {
          const trimmed = GB().trimTransparentCanvas(img);
          if (trimmed?.canvas) finalImg = await GB().canvasToImage(trimmed.canvas);
        } catch (_) {
          /* keep original if trim fails (common on opaque JPGs in some browsers) */
          finalImg = img;
        }
        const id = GB().uid('a');
        const naturalW = finalImg.naturalWidth || finalImg.width;
        const naturalH = finalImg.naturalHeight || finalImg.height;
        if (!naturalW || !naturalH) throw new Error(`Invalid image: ${file.name}`);
        const widthMm = GB().roundMm(GB().pxToMm(naturalW, this.config.defaultDpi));
        const maxW = Math.max(10, this.config.sheetWidthMm - (this.config.safeMarginMm || 0) * 2);
        const fitW = Math.min(widthMm, maxW);
        const asset = {
          id,
          kind: 'image',
          name: file.name.replace(/\.[^.]+$/, ''),
          fileName: file.name,
          imageUrl: finalImg.src || url,
          sourceUrl: finalImg.src || url,
          pixelWidth: naturalW,
          pixelHeight: naturalH,
          widthMm: fitW,
          heightMm: GB().roundMm((fitW / naturalW) * naturalH),
          qty: 1,
          allowAutoRotate: true,
          hue: 0,
          saturation: 100,
          brightness: 100,
        };
        /* Ensure we have a data URL for Konva/cache */
        if (!String(asset.imageUrl).startsWith('data:')) {
          const c = document.createElement('canvas');
          c.width = naturalW;
          c.height = naturalH;
          c.getContext('2d').drawImage(finalImg, 0, 0);
          asset.imageUrl = c.toDataURL('image/png');
          asset.sourceUrl = asset.imageUrl;
          finalImg = await GB().canvasToImage(c);
        }
        this.assets.set(id, asset);
        this.sourceCache.set(id, asset.sourceUrl);
        this.imageCache.set(id, finalImg);
        asset.thumbUrl = this.createAssetThumb(finalImg);
        return id;
      } catch (err) {
        throw err instanceof Error ? err : new Error(String(err));
      }
    }

    async handleFiles(fileList) {
      const files = [...(fileList || [])].filter((f) => /^image\//.test(f.type) || /\.(png|jpe?g|webp|svg)$/i.test(f.name || ''));
      if (!files.length) {
        this.showError('Please choose PNG, JPG, WEBP or SVG image files.');
        return;
      }
      this.clearError();
      this.setActiveRail('uploads');
      this.sheetLengthMode = 'auto';
      if (this.autoLengthCheck) this.autoLengthCheck.checked = true;
      this.syncLengthPresets();
      const added = [];
      for (const file of files) {
        try {
          const id = await this.processImageFile(file);
          if (id) added.push(id);
        } catch (err) {
          this.showError(err.message || `Failed: ${file.name}`);
        }
      }
      this.renderDesignList();
      if (added.length) this.placeNewAssets(added);
      else this.renderAll();
    }

    setActiveRail(rail) {
      this.activeRail = rail || 'uploads';
      this.drawerCollapsed = false;
      this.root.classList.remove('is-drawer-collapsed');
      this.root.querySelectorAll('button[data-pgb-rail]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.pgbRail === this.activeRail);
      });
      this.root.querySelectorAll('[data-pgb-drawer-panel]').forEach((panel) => {
        const on = panel.dataset.pgbDrawerPanel === this.activeRail;
        panel.hidden = !on;
        panel.classList.toggle('is-active', on);
      });
    }

    addTextPreset(preset) {
      const map = {
        title: { text: 'Add a title', fontSizePx: 72, fontFamily: 'Montserrat' },
        subtitle: { text: 'Add a subtitle', fontSizePx: 48, fontFamily: 'Poppins' },
        body: { text: 'Add a little bit of body text', fontSizePx: 28, fontFamily: 'Inter' },
      };
      const cfg = map[preset] || map.body;
      if (this.textInput) this.textInput.value = cfg.text;
      if (this.fontSizeInput) this.fontSizeInput.value = cfg.fontSizePx;
      if (this.fontSelect) this.fontSelect.value = cfg.fontFamily;
      this.addTextItem();
    }

    nestAll(preserveAssetId = null) {
      const nestOpts = {
        ...this.config,
        gapMm: this.spacingMm,
        marginMm: 0,
        defaultSheetLengthMm: this.config.defaultSheetLengthMm,
      };

      /* Re-arrange what's on the sheet. Existing items keep their size, mask and styling —
         we only move them. Quantities are reconciled against each design's Qty first. */
      this.reconcileQuantities({ silent: true });
      let combined = this.items.map((i) => ({ ...i, allowAutoRotate: false }));

      if (combined.length > 1) {
        const repack = Nest().nestItemsInPlace(combined, nestOpts);
        if (repack.error) {
          this.showError(repack.error);
          return;
        }
        const posMap = new Map((repack.placements || []).map((p) => [p.instanceId, p]));
        combined = combined.map((item) => {
          const p = posMap.get(item.instanceId);
          if (!p) return item;
          return { ...item, xMm: p.xMm, yMm: p.yMm, widthMm: p.widthMm, heightMm: p.heightMm };
        });
      } else if (combined.length === 1) {
        combined = [{ ...combined[0], xMm: 0, yMm: 0 }];
      }

      this.clearError();
      this.ensureSheetFitsItems(combined);
      const sheetH = this.sheetHeightMm(combined);
      this.items = combined.map((item) => this.clampItemToSheet(item, sheetH));

      this.selectedIds.clear();
      if (preserveAssetId) {
        this.items.filter((i) => i.id === preserveAssetId).forEach((i) => this.selectedIds.add(i.instanceId));
      }
      this.pushHistory();
      this.renderAll({ skipAutoFix: true });
    }

    newImageInstance(asset, template = null) {
      const base = template || {
        id: asset.id,
        kind: 'image',
        name: asset.name,
        widthMm: asset.widthMm,
        heightMm: asset.heightMm,
        rotationDeg: 0,
      };
      const { instanceId, xMm, yMm, ...rest } = base;
      const pos = this.placeNearOrigin(base.widthMm, base.heightMm);
      return { ...rest, id: asset.id, kind: 'image', instanceId: GB().uid('img'), xMm: pos.xMm, yMm: pos.yMm };
    }

    /* Make the number of instances of each design match its Qty without moving anything else. */
    reconcileQuantities(options = {}) {
      let changed = false;
      [...this.assets.values()].filter((a) => a.kind === 'image' || !a.kind).forEach((asset) => {
        const qty = Math.max(1, Number(asset.qty) || 1);
        const existing = this.items.filter((i) => i.id === asset.id && (!i.kind || i.kind === 'image'));
        if (existing.length < qty) {
          const template = existing[0] ? { ...existing[0] } : null;
          if (template) {
            delete template.maskShapeId;
            delete template.maskPath;
          }
          for (let n = existing.length; n < qty; n += 1) {
            this.items.push(this.newImageInstance(asset, template));
            changed = true;
          }
        } else if (existing.length > qty) {
          const drop = new Set(existing.slice(qty).map((i) => i.instanceId));
          this.items = this.items.filter((i) => !drop.has(i.instanceId));
          drop.forEach((id) => this.selectedIds.delete(id));
          changed = true;
        }
      });
      if (changed && !options.silent) {
        this.ensureSheetFitsItems(this.items);
        this.pushHistory();
        this.renderAll();
      }
      return changed;
    }

    /* Drop newly uploaded designs into free space; everything already on the sheet stays put. */
    placeNewAssets(assetIds) {
      const ids = [...assetIds].filter((id) => this.assets.has(id));
      if (!ids.length) return;
      this.selectedIds.clear();
      ids.forEach((id) => {
        const asset = this.assets.get(id);
        const qty = Math.max(1, Number(asset.qty) || 1);
        for (let n = 0; n < qty; n += 1) {
          const inst = this.newImageInstance(asset);
          this.items.push(inst);
          this.selectedIds.add(inst.instanceId);
        }
      });
      this.ensureSheetFitsItems(this.items);
      this.clearError();
      this.pushHistory();
      this.renderAll();
      const first = this.items.find((i) => this.selectedIds.has(i.instanceId));
      if (first) this.scrollItemIntoView(first.instanceId);
    }

    clampItemToSheet(item, sheetHeightOverride) {
      const sheetW = this.config.sheetWidthMm;
      const sheetH = sheetHeightOverride ?? this.sheetHeightMm();
      const margin = 0;
      const box = GB().aabbFromItem(item);
      let xMm = item.xMm;
      let yMm = item.yMm;
      if (box.width > sheetW - margin * 2) {
        item = { ...item, widthMm: Math.min(item.widthMm, sheetW - margin * 2) };
      }
      const box2 = GB().aabbFromItem({ ...item, xMm, yMm });
      if (box2.x < margin) xMm += margin - box2.x;
      if (box2.y < margin) yMm += margin - box2.y;
      const box3 = GB().aabbFromItem({ ...item, xMm, yMm });
      if (box3.x + box3.width > sheetW - margin) xMm -= (box3.x + box3.width) - (sheetW - margin);
      if (box3.y + box3.height > sheetH - margin) yMm -= (box3.y + box3.height) - (sheetH - margin);
      return { ...item, xMm: GB().roundMm(Math.max(margin, xMm)), yMm: GB().roundMm(Math.max(margin, yMm)) };
    }

    placeNearOrigin(widthMm, heightMm) {
      const margin = 0;
      const sheetW = this.config.sheetWidthMm;
      const fitW = Math.min(widthMm, sheetW - margin * 2);
      const fitH = heightMm;
      const scanH = Math.max(this.sheetHeightMm(), this.config.defaultSheetLengthMm);
      const step = Math.max(5, this.spacingMm);

      for (let y = margin; y + fitH <= scanH + 0.01; y += step) {
        for (let x = margin; x + fitW <= sheetW - margin + 0.01; x += step) {
          const candidate = this.clampItemToSheet({
            xMm: GB().roundMm(x), yMm: GB().roundMm(y), widthMm: fitW, heightMm: fitH, rotationDeg: 0,
          });
          if (this.canPlaceItem(candidate)) return candidate;
        }
      }

      const y = this.items.reduce((max, i) => {
        const box = GB().aabbFromItem(i);
        return Math.max(max, box.y + box.height);
      }, margin) + this.spacingMm;
      return this.clampItemToSheet({
        xMm: margin, yMm: GB().roundMm(y), widthMm: fitW, heightMm: fitH, rotationDeg: 0,
      });
    }

    addTextItem() {
      window.PrintoraGangBuilderLibrary?.ensureFontsLoaded();
      const text = String(this.textInput?.value || 'Your text').trim() || 'Your text';
      const fontFamily = this.fontSelect?.value || 'Montserrat';
      const fontSizePx = Math.max(12, Number(this.fontSizeInput?.value) || 48);
      const fill = this.textColourInput?.value || '#111111';
      const widthMm = GB().roundMm(Math.min(this.config.sheetWidthMm * 0.85, Math.max(50, text.length * fontSizePx * 0.22)));
      const heightMm = GB().roundMm(Math.max(22, fontSizePx * 0.55));
      const pos = this.placeNearOrigin(widthMm, heightMm);
      const instanceId = GB().uid('txt');
      this.items.push({
        kind: 'text',
        id: instanceId,
        instanceId,
        name: text.slice(0, 28),
        text,
        fontFamily,
        fontStyle: `${this.italicOn ? 'italic ' : ''}${this.boldOn ? 'bold' : 'normal'}`.trim(),
        fontSizePx,
        fill,
        stroke: this.outlineOn ? (this.textStrokeInput?.value || '#111111') : null,
        strokeWidth: this.outlineOn ? 2 : 0,
        xMm: pos.xMm,
        yMm: pos.yMm,
        widthMm,
        heightMm,
        rotationDeg: 0,
      });
      this.selectedIds.clear();
      this.selectedIds.add(instanceId);
      this.pushHistory();
      this.renderAll();
      this.syncFormatBar();
      const added = this.items.find((i) => i.instanceId === instanceId);
      if (added) setTimeout(() => this.startInlineTextEdit(added), 40);
    }

    addShapeItem(shapeId) {
      const Lib = window.PrintoraGangBuilderLibrary;
      const shape = Lib?.SHAPES.find((s) => s.id === shapeId);
      if (!shape) return;
      const size = 60;
      const pos = this.placeNearOrigin(size, size);
      const instanceId = GB().uid('shp');
      this.items.push(this.clampItemToSheet({
        kind: 'shape',
        id: instanceId,
        instanceId,
        name: shape.label,
        shapeId: shape.id,
        path: shape.path,
        fill: '#ffffff',
        stroke: this.shapeStrokeInput?.value || '#111111',
        strokeWidth: 2,
        xMm: pos.xMm,
        yMm: pos.yMm,
        widthMm: size,
        heightMm: size,
        rotationDeg: 0,
      }));
      this.selectedIds.clear();
      this.selectedIds.add(instanceId);
      this.pushHistory();
      /* Drop the shape into the nearest free spot; never re-nest the whole sheet or change zoom */
      this.renderAll();
      this.scrollItemIntoView(instanceId);
    }

    scrollItemIntoView(instanceId) {
      const item = this.items.find((i) => i.instanceId === instanceId);
      const scroll = this.stageScroll;
      const host = this.stageHost;
      if (!item || !scroll || !host) return;
      const top = host.offsetTop + this.mmToStageY(item.yMm);
      const bottom = host.offsetTop + this.mmToStageY(item.yMm + item.heightMm);
      const viewTop = scroll.scrollTop;
      const viewBottom = viewTop + scroll.clientHeight;
      if (top < viewTop + 60 || bottom > viewBottom - 20) {
        scroll.scrollTo({ top: Math.max(0, top - Math.max(60, scroll.clientHeight * 0.25)), behavior: 'smooth' });
      }
    }

    addIconItem(iconId) {
      const Lib = window.PrintoraGangBuilderLibrary;
      const icon = Lib?.ICONS.find((i) => i.id === iconId);
      if (!icon) return;
      const size = 40;
      const pos = this.placeNearOrigin(size, size);
      const instanceId = GB().uid('ico');
      this.items.push({
        kind: 'icon',
        id: instanceId,
        instanceId,
        name: icon.label,
        iconId: icon.id,
        path: icon.path,
        fill: 'transparent',
        stroke: this.iconColourInput?.value || '#111111',
        strokeWidth: 2.5,
        xMm: pos.xMm,
        yMm: pos.yMm,
        widthMm: size,
        heightMm: size,
        rotationDeg: 0,
      });
      this.selectedIds.clear();
      this.selectedIds.add(instanceId);
      this.pushHistory();
      this.renderAll();
    }

    updateSelectedGraphicStyle() {
      const selected = this.getSelectedItems();
      if (!selected.length) return;
      let changed = false;
      selected.forEach((item) => {
        if (item.kind === 'text') {
          if (this.textInput?.value) {
            item.text = String(this.textInput.value);
            item.name = item.text.slice(0, 28);
          }
          item.fontFamily = this.fontSelect?.value || item.fontFamily;
          item.fontSizePx = Math.max(12, Number(this.fontSizeInput?.value) || item.fontSizePx || 48);
          item.fill = this.textColourInput?.value || item.fill;
          if (this.outlineOn) {
            item.stroke = this.textStrokeInput?.value || item.stroke || '#111111';
            item.strokeWidth = Math.max(1, item.strokeWidth || 2);
          } else {
            item.stroke = null;
            item.strokeWidth = 0;
          }
          item.fontStyle = `${this.italicOn ? 'italic ' : ''}${this.boldOn ? 'bold' : 'normal'}`.trim();
          changed = true;
        } else if (item.kind === 'shape') {
          item.fill = this.shapeFillInput?.value || this.graphicColourInput?.value || item.fill;
          item.stroke = this.shapeStrokeInput?.value || item.stroke;
          changed = true;
        } else if (item.kind === 'icon') {
          item.stroke = this.iconColourInput?.value || this.graphicColourInput?.value || item.stroke;
          changed = true;
        }
      });
      if (!changed) return;
      this.pushHistory();
      this.renderKonvaItems();
      this.renderDesignList();
      this.syncFormatBar();
    }

    syncFormatBar() {
      if (!this.formatBar) return;
      const selected = this.getSelectedItems();
      const textItem = selected.find((i) => i.kind === 'text');
      const graphic = selected.find((i) => i.kind === 'shape' || i.kind === 'icon');
      const show = Boolean(textItem || graphic);
      this.formatBar.hidden = !show;
      /* Text-only controls are irrelevant for shapes/icons — hide them so the bar reads clearly */
      this.formatBar.querySelectorAll('[data-pgb-text-only]').forEach((el) => { el.hidden = !textItem; });
      const label = this.formatBar.querySelector('[data-pgb-format-label]');
      if (label) {
        label.textContent = textItem ? 'Text' : (graphic?.kind === 'icon' ? 'Icon' : 'Shape');
      }
      if (textItem) {
        if (this.fontSelect) this.fontSelect.value = textItem.fontFamily || 'Montserrat';
        if (this.fontSizeInput) this.fontSizeInput.value = textItem.fontSizePx || 48;
        if (this.textColourInput) this.textColourInput.value = textItem.fill || '#111111';
        const colourChip = this.root.querySelector('[data-pgb-text-colour-chip]');
        if (colourChip) colourChip.style.background = textItem.fill || '#111111';
        this.outlineOn = Number(textItem.strokeWidth) > 0 && Boolean(textItem.stroke);
        if (this.textStrokeInput) this.textStrokeInput.value = textItem.stroke || '#111111';
        const strokeChip = this.root.querySelector('[data-pgb-text-stroke-chip]');
        if (strokeChip) {
          strokeChip.style.background = this.outlineOn ? (textItem.stroke || '#111') : 'transparent';
        }
        if (this.textInput && !this.textInput.matches(':focus') && !this._textEditor) {
          this.textInput.value = textItem.text || '';
        }
        this.boldOn = /bold/i.test(textItem.fontStyle || 'bold');
        this.italicOn = /italic/i.test(textItem.fontStyle || '');
        this.root.querySelector('[data-pgb-bold]')?.classList.toggle('is-active', this.boldOn);
        this.root.querySelector('[data-pgb-italic]')?.classList.toggle('is-active', this.italicOn);
        this.root.querySelector('[data-pgb-outline-toggle]')?.classList.toggle('is-active', this.outlineOn);
      }
      if (this.graphicColourWrap) {
        this.graphicColourWrap.hidden = !graphic;
        if (graphic && this.graphicColourInput) {
          const colour = graphic.fill && graphic.fill !== 'transparent'
            ? graphic.fill
            : (graphic.stroke || '#ed168c');
          this.graphicColourInput.value = colour;
          const chip = this.graphicColourWrap.querySelector('.pgb-format-swatch__chip');
          if (chip) chip.style.background = colour;
        }
      }
    }

    maskSelectionToShape() {
      const selected = this.getSelectedItems();
      const shape = selected.find((i) => i.kind === 'shape');
      const target = selected.find((i) => i.kind === 'image' || !i.kind || i.kind === 'text');
      if (!shape || !target) {
        this.showError('Select a shape and an image (shift+click both), or drag an image onto a shape.');
        return;
      }
      this.applyMaskToItem(target, shape);
    }

    /* Where the image sits inside a mask box of w×h (in whatever unit k converts mm into). */
    maskContentBox(item, w, h, k) {
      const cs = Math.max(0.2, Number(item.contentScale) || 1);
      const aspect = Number(item.contentAspect) || (w / h);
      let cw;
      let ch;
      if (aspect >= w / h) { ch = h; cw = h * aspect; } else { cw = w; ch = w / aspect; }
      cw *= cs;
      ch *= cs;
      const ox = (Number(item.contentOffsetXMm) || 0) * k;
      const oy = (Number(item.contentOffsetYMm) || 0) * k;
      return { x: (w - cw) / 2 + ox, y: (h - ch) / 2 + oy, w: cw, h: ch };
    }

    applyMaskToItem(target, shape) {
      if (!target || !shape) return;
      if (!target.maskShapeId && !target.maskPath) {
        target.contentAspect = target.heightMm > 0 ? target.widthMm / target.heightMm : 1;
        target.contentScale = 1;
        target.contentOffsetXMm = 0;
        target.contentOffsetYMm = 0;
      }
      target.maskShapeId = shape.shapeId;
      target.maskPath = shape.path || null;
      target.xMm = shape.xMm;
      target.yMm = shape.yMm;
      target.widthMm = shape.widthMm;
      target.heightMm = shape.heightMm;
      target.rotationDeg = shape.rotationDeg || 0;
      target.contentScale = target.contentScale || 1;
      target.contentOffsetXMm = target.contentOffsetXMm || 0;
      target.contentOffsetYMm = target.contentOffsetYMm || 0;
      this.items = this.items.filter((i) => i.instanceId !== shape.instanceId);
      this.selectedIds.clear();
      this.selectedIds.add(target.instanceId);
      this.clearError();
      this.pushHistory();
      this.renderAll();
    }

    findShapeUnderItem(item) {
      const box = GB().aabbFromItem(item);
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      let best = null;
      let bestArea = Infinity;
      this.items.forEach((shape) => {
        if (shape.kind !== 'shape') return;
        if (shape.instanceId === item.instanceId) return;
        const sb = GB().aabbFromItem(shape);
        const overlapW = Math.min(box.x + box.width, sb.x + sb.width) - Math.max(box.x, sb.x);
        const overlapH = Math.min(box.y + box.height, sb.y + sb.height) - Math.max(box.y, sb.y);
        if (overlapW <= 0 || overlapH <= 0) return;
        const inside = cx >= sb.x && cx <= sb.x + sb.width && cy >= sb.y && cy <= sb.y + sb.height;
        const coverage = (overlapW * overlapH) / Math.max(1, box.width * box.height);
        if (inside || coverage >= 0.35) {
          const area = sb.width * sb.height;
          if (area < bestArea) {
            bestArea = area;
            best = shape;
          }
        }
      });
      return best;
    }

    /* Build the mask outline as a Path2D in the group's local space (ox/oy = child origin offset). */
    buildMaskPath(item, w, h, ox = 0, oy = 0) {
      if (typeof Path2D === 'undefined') return null;
      const p = new Path2D();
      if (item.maskPath) {
        try {
          if (typeof DOMMatrix !== 'undefined') {
            p.addPath(new Path2D(item.maskPath), new DOMMatrix([w / 100, 0, 0, h / 100, ox, oy]));
            return p;
          }
        } catch (_) { /* fall through to simple shapes */ }
      }
      if (item.maskShapeId === 'circle' || item.maskShapeId === 'ellipse') {
        p.ellipse(ox + w / 2, oy + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      } else if (item.maskShapeId === 'rounded') {
        const r = Math.min(w, h) * 0.15;
        p.moveTo(ox + r, oy);
        p.arcTo(ox + w, oy, ox + w, oy + h, r);
        p.arcTo(ox + w, oy + h, ox, oy + h, r);
        p.arcTo(ox, oy + h, ox, oy, r);
        p.arcTo(ox, oy, ox + w, oy, r);
        p.closePath();
      } else {
        p.rect(ox, oy, w, h);
      }
      return p;
    }

    /* Konva clipFunc: returns the args for ctx.clip(); falls back to drawing the path on the context. */
    drawMaskClip(ctx, item, w, h, ox = 0, oy = 0) {
      const path = this.buildMaskPath(item, w, h, ox, oy);
      if (path) return [path];
      ctx.beginPath();
      if (item.maskShapeId === 'circle' || item.maskShapeId === 'ellipse') {
        ctx.ellipse(ox + w / 2, oy + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      } else {
        ctx.rect(ox, oy, w, h);
      }
      ctx.closePath();
      return undefined;
    }

    wrapVisualNode(inner, item, px, py, w, h) {
      const rot = item.rotationDeg || 0;
      const masked = item.maskShapeId || item.maskPath;
      const group = new Konva.Group({
        x: rot ? px + w / 2 : px,
        y: rot ? py + h / 2 : py,
        width: w,
        height: h,
        rotation: rot,
        draggable: true,
        /* Keep the artwork inside the sheet width while dragging; the sheet grows downwards as needed */
        dragBoundFunc: (pos) => {
          if (this.maskEditId === item.instanceId) return pos;
          const k = this.pxPerMm * this.zoom;
          const box = GB().aabbFromItem(item);
          const originMmX = rot ? item.xMm + item.widthMm / 2 : item.xMm;
          const originMmY = rot ? item.yMm + item.heightMm / 2 : item.yMm;
          const offL = (box.x - originMmX) * k;
          const offT = (box.y - originMmY) * k;
          const sheetWpx = this.config.sheetWidthMm * k;
          const minX = 24 - offL;
          const maxX = 24 + sheetWpx - box.width * k - offL;
          const minY = 20 - offT;
          return {
            x: Math.min(Math.max(pos.x, minX), Math.max(minX, maxX)),
            y: Math.max(pos.y, minY),
          };
        },
      });
      const originX = rot ? -w / 2 : 0;
      const originY = rot ? -h / 2 : 0;
      if (masked) group.clipFunc((ctx) => this.drawMaskClip(ctx, item, w, h, originX, originY));
      /* The item's box (not the unclipped photo inside a mask, nor stroke overhang) drives the
         transformer and hit box, so selection handles always hug the design. */
      group.getClientRect = (cfg = {}) => {
        const local = { x: originX, y: originY, width: w, height: h };
        if (cfg.skipTransform) return local;
        return group._transformedRect(local, cfg.relativeTo);
      };
      if (rot) {
        inner.x(-w / 2);
        inner.y(-h / 2);
      }
      /* Image inside a mask: "cover" fit that keeps the photo's aspect ratio, plus user pan/zoom */
      if (masked && (!item.kind || item.kind === 'image')) {
        const cb = this.maskContentBox(item, w, h, this.pxPerMm * this.zoom);
        const baseX = rot ? -w / 2 : 0;
        const baseY = rot ? -h / 2 : 0;
        inner.x(baseX + cb.x);
        inner.y(baseY + cb.y);
        inner.width(cb.w);
        inner.height(cb.h);
      }
      group.add(inner);
      group.setAttr('pgbUseCenter', Boolean(rot));
      group.setAttr('pgbW', w);
      group.setAttr('pgbH', h);
      return group;
    }

    clearMaskOnSelection() {
      this.getSelectedItems().forEach((item) => {
        delete item.maskShapeId;
        delete item.maskPath;
        /* Give the photo its natural proportions back */
        if ((!item.kind || item.kind === 'image') && Number(item.contentAspect) > 0) {
          item.heightMm = GB().roundMm(item.widthMm / item.contentAspect);
        }
        delete item.contentAspect;
        delete item.contentScale;
        delete item.contentOffsetXMm;
        delete item.contentOffsetYMm;
      });
      this.maskEditId = null;
      this.pushHistory();
      this.renderAll();
    }

    removeAsset(assetId) {
      this.assets.delete(assetId);
      this.imageCache.delete(assetId);
      this.sourceCache.delete(assetId);
      this.items = this.items.filter((i) => i.id !== assetId);
      this.selectedIds.clear();
      this.pushHistory();
      this.renderAll();
    }

    getSelectedItems() { return this.items.filter((i) => this.selectedIds.has(i.instanceId)); }
    getPrimaryAsset() {
      const sel = this.getSelectedItems()[0];
      return sel ? this.assets.get(sel.id) : null;
    }

    selectInstancesByAsset(assetId, additive) {
      const ids = this.items.filter((i) => i.id === assetId).map((i) => i.instanceId);
      if (!ids.length && this.assets.has(assetId)) {
        this.placeNewAssets([assetId]);
        return;
      }
      if (!additive) this.selectedIds.clear();
      ids.forEach((id) => this.selectedIds.add(id));
      this.syncKonvaSelection();
      this.syncDock();
      this.renderDesignList();
      this.stageHost?.focus?.({ preventScroll: true });
    }

    clearSelection() {
      this.selectedIds.clear();
      this.syncKonvaSelection();
      this.syncDock();
    }

    copySelected() { this.clipboard = this.getSelectedItems().map((i) => ({ ...i })); }

    pasteClipboard() {
      if (!this.clipboard.length) return;
      const offset = this.spacingMm;
      this.clipboard.forEach((src) => {
        const instanceId = GB().uid('i');
        this.items.push(this.clampItemToSheet({
          ...src,
          instanceId,
          xMm: GB().roundMm(src.xMm + offset),
          yMm: GB().roundMm(src.yMm + offset),
        }));
        this.selectedIds.add(instanceId);
      });
      this.pushHistory();
      this.renderAll();
    }

    duplicateSelected() { this.copySelected(); this.pasteClipboard(); }

    deleteSelected() {
      if (!this.selectedIds.size) return;
      const removed = this.items.filter((i) => this.selectedIds.has(i.instanceId));
      this.items = this.items.filter((i) => !this.selectedIds.has(i.instanceId));
      removed.forEach((item) => {
        if (item.kind && item.kind !== 'image') return;
        const stillUsed = this.items.some((i) => i.id === item.id);
        if (!stillUsed && this.assets.has(item.id)) {
          this.assets.delete(item.id);
          this.imageCache.delete(item.id);
          this.sourceCache.delete(item.id);
        }
      });
      this.selectedIds.clear();
      this.pushHistory();
      this.renderAll();
    }

    canPlaceItem(item, ignoreIds = new Set()) {
      const sheetH = this.sheetHeightMm();
      const margin = 0; /* allow flush to pink border */
      const box = GB().aabbFromItem(item);
      if (box.x < margin - 0.01 || box.y < margin - 0.01
        || box.x + box.width > this.config.sheetWidthMm - margin + 0.01
        || box.y + box.height > sheetH - margin + 0.01) return false;
      const others = this.items.filter((i) => !ignoreIds.has(i.instanceId) && i.instanceId !== item.instanceId);
      return !others.some((o) => GB().boxesOverlap(box, GB().aabbFromItem(o), this.spacingMm));
    }

    nudgeSelected(dx, dy) {
      if (!this.selectedIds.size) return;
      /* Free movement: nudging never gets blocked, overlaps are reported by the stats check instead */
      this.getSelectedItems().forEach((item) => {
        item.xMm = GB().roundMm(item.xMm + dx);
        item.yMm = GB().roundMm(item.yMm + dy);
        Object.assign(item, this.clampItemToSheet(item));
      });
      this.pushHistory();
      this.renderKonvaItems();
      this.renderStats();
    }

    rotateSelected(deg) {
      this.getSelectedItems().forEach((item) => {
        item.rotationDeg = ((Number(item.rotationDeg) || 0) + deg) % 360;
        if (item.rotationDeg < 0) item.rotationDeg += 360;
      });
      this.pushHistory();
      this.renderAll();
    }

    applyDockSize(heightDriven) {
      const sel = this.getSelectedItems()[0];
      if (!sel) return;
      const w = GB().roundMm(this.unit === 'in' ? GB().inToMm(Number(this.dockWidth.value)) : Number(this.dockWidth.value));
      const h = GB().roundMm(this.unit === 'in' ? GB().inToMm(Number(this.dockHeight.value)) : Number(this.dockHeight.value));
      const asset = this.getPrimaryAsset();

      if (!sel.kind || sel.kind === 'image') {
        if (!asset) return;
        if (!heightDriven && w > 0) {
          sel.widthMm = Math.min(w, this.config.sheetWidthMm);
          sel.heightMm = GB().roundMm((sel.widthMm / asset.pixelWidth) * asset.pixelHeight);
        } else if (h > 0) {
          sel.heightMm = h;
          sel.widthMm = GB().roundMm(Math.min((sel.heightMm / asset.pixelHeight) * asset.pixelWidth, this.config.sheetWidthMm));
        }
      } else if (w > 0 && h > 0) {
        sel.widthMm = Math.min(w, this.config.sheetWidthMm);
        sel.heightMm = h;
      }
      Object.assign(sel, this.clampItemToSheet(sel));
      this.syncDock();
      this.renderKonvaItems();
      this.renderStats();
      this.pushHistory();
    }

    applyDockQty() {
      const asset = this.getPrimaryAsset();
      if (!asset) return;
      asset.qty = Math.max(1, Number(this.dockQty.value) || 1);
      if (!this.reconcileQuantities()) this.renderDesignList();
    }

    syncDock() {
      const selected = this.getSelectedItems();
      const primary = selected[0];
      if (!primary) {
        if (this.dockEmpty) this.dockEmpty.hidden = false;
        if (this.dockForm) this.dockForm.hidden = true;
        return;
      }

      if (this.dockEmpty) this.dockEmpty.hidden = true;
      if (this.dockForm) this.dockForm.hidden = false;

      const isImage = !primary.kind || primary.kind === 'image';
      const asset = isImage ? this.assets.get(primary.id) : null;
      const u = this.unit;

      if (this.dockThumb) {
        this.dockThumb.hidden = !isImage;
        if (isImage && asset) {
          const cached = this.getCachedItemImage(primary);
          this.dockThumb.src = primary.customSource || cached?.src || asset.imageUrl;
        }
      }
      if (this.dockGlyph) {
        this.dockGlyph.hidden = isImage;
        const glyphs = { text: 'T', shape: '◆', icon: '★' };
        this.dockGlyph.textContent = glyphs[primary.kind] || '◆';
      }

      const wVal = primary.widthMm;
      const hVal = primary.heightMm;
      if (this.dockWidth) this.dockWidth.value = u === 'in' ? GB().mmToIn(wVal).toFixed(2) : wVal;
      if (this.dockHeight) this.dockHeight.value = u === 'in' ? GB().mmToIn(hVal).toFixed(2) : hVal;
      const unitLabel = u === 'in' ? 'in' : 'mm';
      if (this.dockWidthLabel) this.dockWidthLabel.textContent = `Width (${unitLabel})`;
      if (this.dockHeightLabel) this.dockHeightLabel.textContent = `Height (${unitLabel})`;

      if (this.dockQtyWrap) this.dockQtyWrap.hidden = !isImage;
      if (isImage && asset && this.dockQty) this.dockQty.value = asset.qty;

      if (this.dockDpi) {
        if (isImage && asset) {
          const dpi = GB().effectiveDpi(asset.pixelWidth, primary.widthMm);
          const q = GB().dpiQuality(dpi, this.config);
          this.dockDpi.hidden = false;
          this.dockDpi.textContent = `${Math.round(dpi)} DPI — ${q.label}`;
          this.dockDpi.className = `pgb-dpi pgb-dpi--${q.level}`;
        } else {
          this.dockDpi.hidden = true;
        }
      }

      if (this.dockImageActions) this.dockImageActions.hidden = !isImage;

      const shape = selected.find((i) => i.kind === 'shape');
      const image = selected.find((i) => !i.kind || i.kind === 'image');
      if (this.dockMaskBtn) {
        const canMask = Boolean(shape && image);
        this.dockMaskBtn.hidden = !canMask;
        this.dockMaskBtn.disabled = !canMask;
      }
      if (this.dockUnmaskBtn) {
        const masked = selected.some((i) => i.maskShapeId || i.maskPath);
        this.dockUnmaskBtn.hidden = !masked;
      }
      if (this.dockRenameBtn) this.dockRenameBtn.hidden = false;
    }

    copyStyle() {
      const item = this.getSelectedItems().find((i) => !i.kind || i.kind === 'image');
      if (!item) {
        this.showError('Select an image to copy its style.');
        return;
      }
      this.styleClipboard = {
        hue: item.hue ?? 0,
        saturation: item.saturation ?? 100,
        brightness: item.brightness ?? 100,
        customSource: item.customSource || null,
        maskShapeId: item.maskShapeId || null,
        maskPath: item.maskPath || null,
      };
      this.clearError();
      if (this.saveStatus) this.saveStatus.textContent = 'Style copied ✓';
    }

    async pasteStyle() {
      if (!this.styleClipboard) {
        this.showError('Copy a style first.');
        return;
      }
      const targets = this.getSelectedItems().filter((i) => !i.kind || i.kind === 'image');
      if (!targets.length) {
        this.showError('Select image(s) to paste style onto.');
        return;
      }
      targets.forEach((item) => {
        item.hue = this.styleClipboard.hue;
        item.saturation = this.styleClipboard.saturation;
        item.brightness = this.styleClipboard.brightness;
        if (this.styleClipboard.customSource) item.customSource = this.styleClipboard.customSource;
        else delete item.customSource;
        if (this.styleClipboard.maskShapeId) {
          item.maskShapeId = this.styleClipboard.maskShapeId;
          item.maskPath = this.styleClipboard.maskPath;
        } else {
          delete item.maskShapeId;
          delete item.maskPath;
        }
        this.clearItemImageCache(item.instanceId);
      });
      await Promise.all(targets.map((item) => this.refreshItemImage(item)));
      this.clearError();
      this.pushHistory();
      this.renderAll();
    }

    async quickRemoveBackground() {
      const item = this.getSelectedItems().find((i) => !i.kind || i.kind === 'image');
      const asset = item ? this.assets.get(item.id) : null;
      if (!item || !asset) {
        this.showError('Select an image to remove its background.');
        return;
      }
      try {
        const src = item.customSource || this.sourceCache.get(asset.id) || asset.sourceUrl || asset.imageUrl;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        /* Detects the backdrop colour(s) at the edges and removes everything connected to them —
           white, grey, coloured or studio. Never touches whites inside the design itself. */
        GB().autoRemoveBackground(canvas);
        const trimmed = GB().trimTransparentCanvas(await GB().canvasToImage(canvas));
        const final = await GB().canvasToImage(trimmed.canvas);
        item.customSource = final.src;
        this.clearItemImageCache(item.instanceId);
        await this.refreshItemImage(item);
        this.clearError();
        this.pushHistory();
        this.renderAll();
      } catch (err) {
        this.showError(err.message || 'Background removal failed.');
      }
    }

    displayLen(mm) {
      if (mm >= 1000) return `${(mm / 1000).toFixed(2)} m`;
      if (this.unit === 'in') return `${GB().mmToIn(mm).toFixed(2)} in`;
      return `${GB().roundMm(mm)} mm`;
    }

    renderDesignList() {
      if (!this.designList) return;
      const assets = [...this.assets.values()];
      const graphics = this.items.filter((i) => i.kind && i.kind !== 'image');
      if (this.designBadge) this.designBadge.textContent = String(assets.length + graphics.length);
      if (!assets.length && !graphics.length) {
        this.designList.innerHTML = '<p class="pgb-empty">Upload designs to begin.</p>';
        return;
      }
      const u = this.unit === 'in' ? 'in' : 'mm';
      const assetHtml = assets.map((a) => {
        const selected = this.getSelectedItems().some((i) => i.id === a.id);
        const wDisp = this.unit === 'in' ? GB().mmToIn(a.widthMm).toFixed(2) : Number(a.widthMm).toFixed(1);
        const hDisp = this.unit === 'in' ? GB().mmToIn(a.heightMm).toFixed(2) : Number(a.heightMm).toFixed(1);
        const dpi = Math.round(GB().effectiveDpi(a.pixelWidth, a.widthMm));
        const shortName = a.name.length > 22 ? `${a.name.slice(0, 20)}…` : a.name;
        const safeName = escapeHtml(a.name);
        const safeShort = escapeHtml(shortName);
        const thumb = escapeHtml(a.thumbUrl || a.imageUrl || '');
        return `<article class="pgb-design${selected ? ' is-selected' : ''}" data-pgb-asset-id="${escapeHtml(a.id)}">
          <img src="${thumb}" alt="${safeName}" loading="lazy" decoding="async" width="56" height="56">
          <div class="pgb-design__meta"><strong title="${safeName} — double-click to rename">${safeShort}</strong><span>${wDisp}×${hDisp} ${u} · Qty ${a.qty} · ${dpi} DPI</span></div>
          <div class="pgb-design__actions">
            <button type="button" class="pgb-icon-btn pgb-icon-btn--quiet" data-pgb-rename title="Rename this design" aria-label="Rename">✎</button>
            <button type="button" class="pgb-icon-btn" data-pgb-remove title="Remove this design from the sheet" aria-label="Remove">✕</button>
          </div>
        </article>`;
      }).join('');
      const graphicHtml = graphics.map((g) => {
        const selected = this.selectedIds.has(g.instanceId);
        const wDisp = this.unit === 'in' ? GB().mmToIn(g.widthMm).toFixed(2) : Number(g.widthMm).toFixed(1);
        const hDisp = this.unit === 'in' ? GB().mmToIn(g.heightMm).toFixed(2) : Number(g.heightMm).toFixed(1);
        const label = g.kind === 'text' ? `Text · ${g.text}` : g.kind === 'icon' ? `Icon · ${g.name}` : `Shape · ${g.name}`;
        const short = label.length > 22 ? `${label.slice(0, 20)}…` : label;
        return `<article class="pgb-design${selected ? ' is-selected' : ''}" data-pgb-instance-id="${escapeHtml(g.instanceId)}">
          <div class="pgb-design__glyph" aria-hidden="true">${g.kind === 'text' ? 'T' : g.kind === 'icon' ? '★' : '◆'}</div>
          <div class="pgb-design__meta"><strong title="${escapeHtml(label)} — double-click to rename">${escapeHtml(short)}</strong><span>${wDisp}×${hDisp} ${u}${(g.maskShapeId || g.maskPath) ? ' · masked' : ''}</span></div>
          <div class="pgb-design__actions">
            <button type="button" class="pgb-icon-btn pgb-icon-btn--quiet" data-pgb-rename title="Rename" aria-label="Rename">✎</button>
            <button type="button" class="pgb-icon-btn" data-pgb-remove-instance title="Remove from the sheet" aria-label="Remove">✕</button>
          </div>
        </article>`;
      }).join('');
      this.designList.innerHTML = assetHtml + graphicHtml;
    }

    applyItemClip(node, item) {
      /* Legacy — clipping handled via Group.wrapVisualNode */
    }

    createGraphicNode(item) {
      const px = this.mmToStageX(item.xMm);
      const py = this.mmToStageY(item.yMm);
      const w = item.widthMm * this.pxPerMm * this.zoom;
      const h = item.heightMm * this.pxPerMm * this.zoom;
      let inner = null;

      if (item.kind === 'text') {
        const fontSize = Math.max(10, (item.fontSizePx || 48) * (this.pxPerMm * this.zoom) / 3.2);
        const strokeW = Number(item.strokeWidth) > 0 ? Number(item.strokeWidth) : 0;
        inner = new Konva.Text({
          x: 0, y: 0,
          text: item.text || 'Text',
          fontFamily: item.fontFamily || 'Montserrat',
          fontStyle: item.fontStyle || 'bold',
          fontSize,
          fill: item.fill || '#111111',
          stroke: strokeW > 0 ? (item.stroke || '#111111') : undefined,
          strokeWidth: strokeW,
          wrap: 'none',
          listening: true,
          name: item.instanceId,
        });
        /* Auto-fit box to actual text size so it doesn't wrap oddly */
        const tw = Math.max(inner.width(), 8);
        const th = Math.max(inner.height(), 8);
        item.widthMm = GB().roundMm(tw / (this.pxPerMm * this.zoom));
        item.heightMm = GB().roundMm(th / (this.pxPerMm * this.zoom));
      } else if (item.kind === 'shape') {
        if (item.shapeId === 'circle' || item.shapeId === 'ellipse') {
          inner = new Konva.Ellipse({
            x: w / 2, y: h / 2, radiusX: w / 2, radiusY: h / 2,
            fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth || 2,
            name: item.instanceId,
          });
        } else if (item.shapeId === 'rounded') {
          inner = new Konva.Rect({
            x: 0, y: 0, width: w, height: h, cornerRadius: Math.min(w, h) * 0.15,
            fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth || 2,
            name: item.instanceId,
          });
        } else if (item.path) {
          inner = new Konva.Path({
            x: 0, y: 0, data: item.path,
            fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth || 2,
            scaleX: w / 100, scaleY: h / 100,
            name: item.instanceId,
          });
          inner.setAttr('pgbPathBox', true);
        } else {
          inner = new Konva.Rect({
            x: 0, y: 0, width: w, height: h,
            fill: item.fill, stroke: item.stroke, strokeWidth: item.strokeWidth || 2,
            name: item.instanceId,
          });
        }
      } else if (item.kind === 'icon') {
        inner = new Konva.Path({
          x: 0, y: 0, data: item.path,
          fill: item.fill || 'transparent',
          stroke: item.stroke || '#111',
          strokeWidth: item.strokeWidth || 2,
          scaleX: w / 24, scaleY: h / 24,
          name: item.instanceId,
        });
        inner.setAttr('pgbPathBox', true);
      }

      if (!inner) return null;
      const finalW = item.kind === 'text'
        ? item.widthMm * this.pxPerMm * this.zoom
        : w;
      const finalH = item.kind === 'text'
        ? item.heightMm * this.pxPerMm * this.zoom
        : h;
      const node = this.wrapVisualNode(inner, item, px, py, finalW, finalH);
      node.setAttr('instanceId', item.instanceId);
      node.setAttr('pgbKind', item.kind);
      if (item.kind === 'text') {
        node.on('dblclick dbltap', (e) => {
          e.cancelBubble = true;
          this.startInlineTextEdit(item);
        });
      }
      return node;
    }

    closeInlineTextEdit(commit = true) {
      if (!this._textEditor) return;
      const { el, itemId } = this._textEditor;
      /* Clear first: removing the textarea fires blur, which calls back into this method */
      this._textEditor = null;
      const item = this.items.find((i) => i.instanceId === itemId);
      if (commit && item && item.kind === 'text') {
        item.text = String(el.value || '').trim() || 'Text';
        item.name = item.text.slice(0, 28);
        if (this.textInput) this.textInput.value = item.text;
        this.pushHistory();
      }
      try { if (el.parentNode) el.parentNode.removeChild(el); } catch (_) {}
      this.renderKonvaItems();
      this.syncDock();
      this.syncFormatBar();
      this.renderDesignList();
    }

    startInlineTextEdit(item) {
      if (!item || item.kind !== 'text' || !this.stageHost) return;
      this.closeInlineTextEdit(true);
      this.selectedIds.clear();
      this.selectedIds.add(item.instanceId);
      this.syncKonvaSelection();
      this.syncFormatBar();

      const host = this.stageHost;
      /* The editor lives inside the scroll container, so it scrolls with the canvas */
      const left = host.offsetLeft + this.mmToStageX(item.xMm);
      const top = host.offsetTop + this.mmToStageY(item.yMm);
      const fontPx = Math.max(12, (item.fontSizePx || 48) * (this.pxPerMm * this.zoom) / 3.2);

      const el = document.createElement('textarea');
      el.className = 'pgb-inline-text';
      el.value = item.text || '';
      el.style.cssText = [
        'position:absolute',
        `left:${Math.max(0, left)}px`,
        `top:${Math.max(0, top)}px`,
        `min-width:${Math.max(80, item.widthMm * this.pxPerMm * this.zoom)}px`,
        `min-height:${Math.max(28, item.heightMm * this.pxPerMm * this.zoom)}px`,
        `font-family:${item.fontFamily || 'Montserrat'}, sans-serif`,
        `font-size:${fontPx}px`,
        `font-weight:${/bold/i.test(item.fontStyle || '') ? '700' : '400'}`,
        `font-style:${/italic/i.test(item.fontStyle || '') ? 'italic' : 'normal'}`,
        `color:${item.fill || '#111'}`,
        'line-height:1.15',
        'padding:2px 4px',
        'margin:0',
        'border:2px solid #ed168c',
        'border-radius:6px',
        'background:rgba(255,255,255,0.96)',
        'outline:none',
        'resize:both',
        'z-index:30',
        'box-shadow:0 4px 16px rgba(0,0,0,0.12)',
      ].join(';');

      const wrap = host.parentElement || host;
      if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
      wrap.appendChild(el);
      this._textEditor = { el, itemId: item.instanceId };
      el.focus();
      el.select();
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeInlineTextEdit(false);
        } else if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.closeInlineTextEdit(true);
        }
        e.stopPropagation();
      });
      el.addEventListener('blur', () => this.closeInlineTextEdit(true));
    }

    startTextEdit(item) {
      this.startInlineTextEdit(item);
    }

    bindItemNodeEvents(node, item) {
      node.on('mousedown touchstart', (e) => {
        e.cancelBubble = true;
        const additive = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
        if (!additive && !this.selectedIds.has(item.instanceId)) {
          this.selectedIds.clear();
          this.selectedIds.add(item.instanceId);
          this.syncKonvaSelection();
          this.syncDock();
          this.syncFormatBar();
          this.renderDesignList();
        }
      });
      node.on('click tap', (e) => {
        e.cancelBubble = true;
        const additive = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
        if (!additive) this.selectedIds.clear();
        if (this.selectedIds.has(item.instanceId) && additive) this.selectedIds.delete(item.instanceId);
        else this.selectedIds.add(item.instanceId);
        this.syncKonvaSelection();
        this.syncDock();
        this.syncFormatBar();
        this.renderDesignList();
      });
      node.on('dragstart', () => {
        if (this._textEditor) this.closeInlineTextEdit(true);
        this.dragSnapshot = this.getSelectedItems().map((i) => ({
          instanceId: i.instanceId,
          xMm: i.xMm,
          yMm: i.yMm,
          widthMm: i.widthMm,
          heightMm: i.heightMm,
        }));
        this.dragSnapshotCount = this.dragSnapshot.length;
        this.dragNodeStart = { x: node.x(), y: node.y(), id: item.instanceId };
        this.stageScroll?.classList.add('is-dragging-item');
        node.moveToTop();
        this.transformer?.moveToTop();
      });
      node.on('dragmove', () => {
        if (!this.dragSnapshot || this.dragNodeStart?.id !== item.instanceId) return;
        const dx = node.x() - this.dragNodeStart.x;
        const dy = node.y() - this.dragNodeStart.y;
        if (this.maskEditId === item.instanceId) {
          /* Pan the photo inside the mask; the mask itself stays put */
          const inner = typeof node.getChildren === 'function' ? node.getChildren()[0] : null;
          if (!this._maskPan) this._maskPan = { dx: 0, dy: 0, baseX: inner?.x() || 0, baseY: inner?.y() || 0 };
          this._maskPan.dx = dx;
          this._maskPan.dy = dy;
          node.position({ x: this.dragNodeStart.x, y: this.dragNodeStart.y });
          if (inner) inner.position({ x: this._maskPan.baseX + this._maskPan.dx, y: this._maskPan.baseY + this._maskPan.dy });
          return;
        }
        const selectedNodes = this.layer.getChildren().filter((n) => this.selectedIds.has(n.getAttr('instanceId')));
        selectedNodes.forEach((n) => {
          if (n === node) return;
          const snap = this.dragSnapshot.find((s) => s.instanceId === n.getAttr('instanceId'));
          if (!snap) return;
          n.position({
            x: this.mmToStageX(snap.xMm) + dx,
            y: this.mmToStageY(snap.yMm) + dy,
          });
        });
      });
      node.on('dragend', () => {
        this.stageScroll?.classList.remove('is-dragging-item');
        this.commitDrag(node, item);
      });
    }

    renderKonvaItems() {
      if (!this.layer) return;
      this.redrawSheetBackground();
      this.layer.destroyChildren();

      this.items.forEach((item) => {
        const w = item.widthMm * this.pxPerMm * this.zoom;
        const h = item.heightMm * this.pxPerMm * this.zoom;
        const px = this.mmToStageX(item.xMm);
        const py = this.mmToStageY(item.yMm);
        let node = null;
        if (!item.kind || item.kind === 'image') {
          const img = this.getCachedItemImage(item);
          if (!img) return;
          const inner = new Konva.Image({
            image: img,
            x: 0,
            y: 0,
            width: w,
            height: h,
            name: item.instanceId,
          });
          node = this.wrapVisualNode(inner, item, px, py, w, h);
          node.setAttr('instanceId', item.instanceId);
          node.setAttr('pgbKind', 'image');
          if (item.maskShapeId || item.maskPath) {
            node.on('dblclick dbltap', (e) => {
              e.cancelBubble = true;
              this.enterMaskContentEdit(item);
            });
          }
        } else {
          node = this.createGraphicNode(item);
          if (!node) return;
          if (!node.getAttr('instanceId')) {
            node.setAttr('instanceId', item.instanceId);
            node.setAttr('pgbKind', item.kind);
          }
        }

        /* Outline so unselected artwork edges are visible against the checkerboard */
        const isSelected = this.selectedIds.has(item.instanceId);
        if (this.showOutlines && !isSelected && !node.getClassName().includes('Group')) {
          node.stroke('#6b6b75');
          node.strokeWidth(1.25);
          node.dash([5, 4]);
        } else if ((!item.kind || item.kind === 'image') && isSelected && !node.getClassName().includes('Group')) {
          node.stroke(null);
          node.dash(null);
        }

        this.bindItemNodeEvents(node, item);
        this.layer.add(node);
      });

      this.layer.batchDraw();
      this.syncKonvaSelection();
    }

    commitDrag(node, primaryItem) {
      if (!this.dragSnapshot) return;

      /* Mask edit mode: dragging pans the photo inside the shape instead of moving the shape */
      if (this.maskEditId === primaryItem.instanceId && this.dragNodeStart?.id === primaryItem.instanceId) {
        const k = this.pxPerMm * this.zoom;
        const pan = this._maskPan || { dx: node.x() - this.dragNodeStart.x, dy: node.y() - this.dragNodeStart.y };
        this._maskPan = null;
        primaryItem.contentOffsetXMm = GB().roundMm((Number(primaryItem.contentOffsetXMm) || 0) + pan.dx / k);
        primaryItem.contentOffsetYMm = GB().roundMm((Number(primaryItem.contentOffsetYMm) || 0) + pan.dy / k);
        this.dragSnapshot = null;
        this.pushHistory();
        this.renderKonvaItems();
        return;
      }

      const useCenter = node.getAttr('pgbUseCenter');
      const w = primaryItem.widthMm;
      const h = primaryItem.heightMm;
      let newX;
      let newY;
      if (useCenter) {
        newX = GB().roundMm(this.stageToMmX(node.x()) - w / 2);
        newY = GB().roundMm(this.stageToMmY(node.y()) - h / 2);
      } else if (node.getAttr('pgbAnchor') === 'center') {
        newX = GB().roundMm(this.stageToMmX(node.x()) - w / 2);
        newY = GB().roundMm(this.stageToMmY(node.y()) - h / 2);
      } else {
        newX = GB().roundMm(this.stageToMmX(node.x()));
        newY = GB().roundMm(this.stageToMmY(node.y()));
      }
      if (this.snapEdges) {
        const margin = this.config.safeMarginMm || 0;
        const sheetW = this.config.sheetWidthMm;
        const sheetH = this.sheetHeightMm();
        const box = GB().aabbFromItem({ ...primaryItem, xMm: newX, yMm: newY });
        if (Math.abs(box.x - margin) < 3) newX = GB().roundMm(margin + (w - box.width) / 2);
        if (Math.abs(box.y - margin) < 3) newY = GB().roundMm(margin + (h - box.height) / 2);
        if (Math.abs(box.x + box.width - (sheetW - margin)) < 3) {
          newX = GB().roundMm(sheetW - margin - (w + box.width) / 2);
        }
        if (Math.abs(box.y + box.height - (sheetH - margin)) < 3) {
          newY = GB().roundMm(sheetH - margin - (h + box.height) / 2);
        }
      }
      const dx = newX - primaryItem.xMm;
      const dy = newY - primaryItem.yMm;
      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
        this.dragSnapshot = null;
        this.renderKonvaItems();
        return;
      }
      /* Free movement: drop exactly where the user let go (kept inside the sheet width).
         Overlaps are allowed — they're flagged by the stats check, and dropping an image on a
         shape is how masking works. */
      const sheetW = this.config.sheetWidthMm;
      this.dragSnapshot.forEach((snap) => {
        const item = this.items.find((i) => i.instanceId === snap.instanceId);
        if (!item) return;
        item.xMm = GB().roundMm(snap.xMm + dx);
        item.yMm = GB().roundMm(snap.yMm + dy);
        const box = GB().aabbFromItem(item);
        if (box.x < 0) item.xMm = GB().roundMm(item.xMm - box.x);
        else if (box.x + box.width > sheetW) item.xMm = GB().roundMm(item.xMm - (box.x + box.width - sheetW));
        if (box.y < 0) item.yMm = GB().roundMm(item.yMm - box.y);
      });
      this.dragSnapshot = null;

      const moved = this.items.find((i) => i.instanceId === primaryItem.instanceId);
      if (moved && this.dragSnapshotCount === 1) {
        /* Drop image onto a shape → auto mask */
        if ((!moved.kind || moved.kind === 'image') && !moved.maskShapeId && !moved.maskPath) {
          const shape = this.findShapeUnderItem(moved);
          if (shape) {
            this.applyMaskToItem(moved, shape);
            return;
          }
        }
        /* Drop shape onto an image → mask that image with the shape */
        if (moved.kind === 'shape') {
          const image = this.findImageUnderShape(moved);
          if (image) {
            this.applyMaskToItem(image, moved);
            return;
          }
        }
      }

      this.pushHistory();
      this.renderKonvaItems();
      this.renderStats();
    }

    findImageUnderShape(shape) {
      const sBox = GB().aabbFromItem(shape);
      const cx = sBox.x + sBox.width / 2;
      const cy = sBox.y + sBox.height / 2;
      const candidates = this.items.filter((i) => (
        i.instanceId !== shape.instanceId
        && (!i.kind || i.kind === 'image')
        && !i.maskShapeId && !i.maskPath
      ));
      let best = null;
      let bestArea = Infinity;
      candidates.forEach((img) => {
        const b = GB().aabbFromItem(img);
        const inside = cx >= b.x && cx <= b.x + b.width && cy >= b.y && cy <= b.y + b.height;
        if (!inside) return;
        const area = b.width * b.height;
        if (area < bestArea) { best = img; bestArea = area; }
      });
      return best;
    }

    enterMaskContentEdit(item) {
      if (!item || !(item.maskShapeId || item.maskPath)) return;
      this.maskEditId = item.instanceId;
      this.selectedIds.clear();
      this.selectedIds.add(item.instanceId);
      this.syncKonvaSelection();
      this.showError('Mask edit: drag to move the photo inside the shape, drag a corner to zoom it. Click empty space when done.');
      setTimeout(() => this.clearError(), 3600);
    }

    itemImageKey(item) {
      const asset = this.assets.get(item.id);
      const hue = item.hue ?? asset?.hue ?? 0;
      const sat = item.saturation ?? asset?.saturation ?? 100;
      const bright = item.brightness ?? asset?.brightness ?? 100;
      if (hue === 0 && sat === 100 && bright === 100 && !item.customSource) return item.id;
      return `${item.id}:${item.instanceId}`;
    }

    getCachedItemImage(item) {
      const key = this.itemImageKey(item);
      return this.imageCache.get(key) || this.imageCache.get(item.id) || null;
    }

    async refreshItemImage(item) {
      const asset = this.assets.get(item.id);
      if (!asset) return;
      const src = item.customSource || this.sourceCache.get(asset.id) || asset.sourceUrl || asset.imageUrl;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      const adjusted = GB().applyColorAdjust(canvas, {
        hue: item.hue ?? asset.hue ?? 0,
        saturation: item.saturation ?? asset.saturation ?? 100,
        brightness: item.brightness ?? asset.brightness ?? 100,
      });
      const final = await GB().canvasToImage(adjusted);
      this.imageCache.set(this.itemImageKey(item), final);
    }

    clearItemImageCache(instanceId) {
      [...this.imageCache.keys()].forEach((key) => {
        if (key.endsWith(`:${instanceId}`)) this.imageCache.delete(key);
      });
    }

    syncKonvaSelection() {
      if (!this.transformer || !this.layer) return;
      const selected = this.layer.getChildren().filter((n) => this.selectedIds.has(n.getAttr('instanceId')));
      this.transformer.nodes(selected.length === 1 ? selected : []);
      this.uiLayer?.find('.selection-outline').forEach((n) => n.destroy());
      /* Always show a clear pink selection outline for every selected item */
      this.getSelectedItems().forEach((item) => {
        const box = GB().aabbFromItem(item);
        this.uiLayer.add(new Konva.Rect({
          x: this.mmToStageX(box.x) - 2,
          y: this.mmToStageY(box.y) - 2,
          width: box.width * this.pxPerMm * this.zoom + 4,
          height: box.height * this.pxPerMm * this.zoom + 4,
          stroke: '#ed168c',
          strokeWidth: selected.length > 1 ? 3 : 2,
          dash: selected.length > 1 ? [7, 4] : undefined,
          fill: selected.length > 1 ? 'rgba(237,22,140,0.06)' : 'transparent',
          listening: false,
          name: 'selection-outline',
          cornerRadius: 2,
        }));
      });
      this.transformer?.moveToTop();
      this.uiLayer?.batchDraw();
      this.syncFormatBar();
    }

    onStagePointerDown(e) {
      if (e.target.findAncestor('Transformer')) return;
      if (e.target.getAttr('instanceId')) return;
      if (e.target.getClassName() !== 'Stage') return;
      if (this.maskEditId) this.maskEditId = null;
      if (this._textEditor) this.closeInlineTextEdit(true);
      this.isSelecting = true;
      const pos = this.stage.getPointerPosition();
      if (!pos) return;
      this.selectStart = pos;
      if (this.selectRect) {
        this.selectRect.destroy();
        this.selectRect = null;
      }
      this.selectRect = new Konva.Rect({
        x: pos.x, y: pos.y, width: 0, height: 0,
        fill: 'rgba(237,22,140,0.12)', stroke: '#ed168c', strokeWidth: 1,
        listening: false,
      });
      this.uiLayer.add(this.selectRect);
      this.selectRect.moveToTop();
      if (!e.evt.shiftKey && !e.evt.ctrlKey && !e.evt.metaKey) this.selectedIds.clear();
    }

    onStagePointerMove() {
      if (!this.isSelecting || !this.selectRect || !this.selectStart) return;
      const pos = this.stage.getPointerPosition();
      if (!pos) return;
      this.selectRect.setAttrs({
        x: Math.min(pos.x, this.selectStart.x),
        y: Math.min(pos.y, this.selectStart.y),
        width: Math.abs(pos.x - this.selectStart.x),
        height: Math.abs(pos.y - this.selectStart.y),
      });
      this.uiLayer.batchDraw();
    }

    onStagePointerUp() {
      const wasSelecting = this.isSelecting;
      this.isSelecting = false;
      /* Only a marquee selection on the canvas needs work here — other clicks (buttons, inputs) must not re-render the UI */
      if (!wasSelecting && !this.selectRect) return;
      if (wasSelecting && this.selectRect) {
        const box = this.selectRect.getClientRect();
        if (box.width > 4 && box.height > 4) {
          this.layer?.getChildren().forEach((node) => {
            const nb = node.getClientRect();
            if (nb.x < box.x + box.width && nb.x + nb.width > box.x
              && nb.y < box.y + box.height && nb.y + nb.height > box.y) {
              this.selectedIds.add(node.getAttr('instanceId'));
            }
          });
        }
      }
      if (this.selectRect) {
        this.selectRect.destroy();
        this.selectRect = null;
        this.uiLayer?.batchDraw();
      }
      this.syncKonvaSelection();
      this.syncDock();
      this.renderDesignList();
    }

    onTransformEnd() {
      const minMm = 10;
      this.transformer.nodes().forEach((node) => {
        const item = this.items.find((i) => i.instanceId === node.getAttr('instanceId'));
        if (!item) return;

        /* Mask content edit: scale/pan image inside the shape, keep mask size */
        if (this.maskEditId === item.instanceId && (item.maskShapeId || item.maskPath)) {
          const scale = Math.max(0.2, Math.min(6, Math.abs(node.scaleX()) || 1));
          item.contentScale = GB().roundMm((Number(item.contentScale) || 1) * scale);
          node.scaleX(1);
          node.scaleY(1);
          this.maskEditId = null;
          this.pushHistory();
          this.renderKonvaItems();
          this.syncDock();
          return;
        }

        const scaleX = Math.abs(node.scaleX()) || 1;
        const scaleY = Math.abs(node.scaleY()) || 1;
        const baseW = Number(node.getAttr('pgbW'))
          || (typeof node.width === 'function' ? node.width() : 0)
          || (item.widthMm * this.pxPerMm * this.zoom);
        const baseH = Number(node.getAttr('pgbH'))
          || (typeof node.height === 'function' ? node.height() : 0)
          || (item.heightMm * this.pxPerMm * this.zoom);

        let newW = GB().roundMm((baseW * scaleX) / (this.pxPerMm * this.zoom));
        let newH = GB().roundMm((baseH * scaleY) / (this.pxPerMm * this.zoom));
        if (!Number.isFinite(newW) || newW <= 0) newW = item.widthMm;
        if (!Number.isFinite(newH) || newH <= 0) newH = item.heightMm;

        const aspect = Math.max(0.05, (item.widthMm || 1) / (item.heightMm || 1));
        if (newW < minMm || newH < minMm) {
          if (newW / aspect < newH) {
            newW = minMm;
            newH = GB().roundMm(minMm / aspect);
          } else {
            newH = minMm;
            newW = GB().roundMm(minMm * aspect);
          }
        }
        newW = Math.min(newW, this.config.sheetWidthMm);
        item.widthMm = newW;
        item.heightMm = newH;

        const useCenter = node.getAttr('pgbUseCenter');
        if (useCenter) {
          item.xMm = GB().roundMm(this.stageToMmX(node.x()) - item.widthMm / 2);
          item.yMm = GB().roundMm(this.stageToMmY(node.y()) - item.heightMm / 2);
        } else {
          item.xMm = GB().roundMm(this.stageToMmX(node.x()));
          item.yMm = GB().roundMm(this.stageToMmY(node.y()));
        }
        Object.assign(item, this.clampItemToSheet(item));
        node.scaleX(1);
        node.scaleY(1);
        node.setAttr('pgbW', item.widthMm * this.pxPerMm * this.zoom);
        node.setAttr('pgbH', item.heightMm * this.pxPerMm * this.zoom);
      });
      this.pushHistory();
      this.renderKonvaItems();
      this.syncDock();
      this.renderStats();
    }

    renderStats(skipAutoFix = false) {
      const used = GB().usedLengthMm(this.items, this.config.safeMarginMm);
      this.ensureSheetFitsItems(this.items);
      const sheetH = Math.max(this.sheetHeightMm(), used + this.config.safeMarginMm);
      const billLen = Math.max(used, this.config.defaultSheetLengthMm);
      const pricing = GB().priceGangSheet(billLen, this.config);
      const eff = GB().utilisation(this.items, this.config.sheetWidthMm, sheetH);
      const collisions = GB().hasCollisions(
        this.items, this.config.sheetWidthMm, sheetH, this.config.safeMarginMm, this.spacingMm
      );

      if (this.lengthEl) this.lengthEl.textContent = `${this.config.sheetWidthMm} × ${this.displayLen(sheetH)}`;
      if (this.totalEl) this.totalEl.textContent = money(pricing.price);

      const ready = this.items.length > 0 && !collisions;
      if (this.addBtn) {
        this.addBtn.disabled = !ready;
        this.addBtn.textContent = ready ? `Add Gang Sheet — ${money(pricing.price)}` : 'Add Gang Sheet';
      }
      if (this.downloadBtn) this.downloadBtn.disabled = !this.items.length;
      if (this.variantInput) this.variantInput.value = this.config.variantId;
      if (this.qtyInput) this.qtyInput.value = String(GB().cartQuantityForLength(billLen, this.config));

      if (collisions && this.items.length) this.showError('Designs overlap or cross sheet edge.');
      else if (ready) this.clearError();

      this.renderPricingPanel(pricing, sheetH, used, eff);
      this.updateCartProps(billLen, pricing);
    }

    renderPricingPanel(pricing, sheetH, used, eff) {
      const upsell = GB().pricingUpsell(
        Math.max(used, this.config.defaultSheetLengthMm),
        this.config
      );
      if (this.pricingTotal) this.pricingTotal.textContent = money(pricing.price);
      if (this.pricingSize) this.pricingSize.textContent = `${this.config.sheetWidthMm} × ${this.displayLen(sheetH)}`;
      if (this.pricingUsed) this.pricingUsed.textContent = this.displayLen(used);
      if (this.pricingBillable) this.pricingBillable.textContent = `${pricing.billableMetres.toFixed(2)} m`;
      if (this.pricingRate) this.pricingRate.textContent = `£${pricing.rate.toFixed(2)}/m`;
      if (this.pricingDesigns) this.pricingDesigns.textContent = String(this.items.length);
      if (this.pricingEff) this.pricingEff.textContent = `${eff}%`;

      if (this.tierList) {
        this.tierList.innerHTML = upsell.tiers.map((t) =>
          `<div class="pgb-tier${t.active ? ' is-active' : ''}"><span>${t.label}</span><span>£${t.rate.toFixed(2)}/m</span></div>`
        ).join('');
      }

      if (this.upsellBox) {
        const msg = upsell.messages[0];
        if (msg) {
          this.upsellBox.hidden = false;
          this.upsellBox.innerHTML = `${msg.text}${msg.highlight ? `<strong>${msg.highlight}</strong>` : ''}`;
        } else {
          this.upsellBox.hidden = true;
        }
      }
    }

    updateCartProps(len, pricing) {
      if (!this.propsHost) return;
      const props = {
        'Sheet Size': `${this.config.sheetWidthMm} mm × ${Math.round(len)} mm`,
        'Design Count': String(this.items.length),
        'Quoted price': money(pricing.price),
        'Rate per metre': `£${pricing.rate.toFixed(2)}`,
        '_layout_hash': String(Date.now()).slice(-8),
      };
      this.propsHost.innerHTML = Object.entries(props).map(([k, v]) =>
        `<input type="hidden" name="properties[${k}]" value="${String(v).replace(/"/g, '&quot;')}">`
      ).join('');
    }

    async refreshAssetImage(asset) {
      const src = this.sourceCache.get(asset.id) || asset.sourceUrl || asset.imageUrl;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const adjusted = GB().applyColorAdjust(canvas, {
        hue: asset.hue || 0,
        saturation: asset.saturation ?? 100,
        brightness: asset.brightness ?? 100,
      });
      const final = await GB().canvasToImage(adjusted);
      asset.imageUrl = final.src;
      asset.pixelWidth = final.naturalWidth;
      asset.pixelHeight = final.naturalHeight;
      this.imageCache.set(asset.id, final);
    }

    async removeBackground() {
      this.openBgModal();
    }

    closeAdjustModal() {
      if (this.adjustModal) this.adjustModal.hidden = true;
      this.adjustSnapshot = null;
    }

    openAdjustModal() {
      const sel = this.getSelectedItems().find((i) => !i.kind || i.kind === 'image');
      const asset = sel ? this.assets.get(sel.id) : this.getPrimaryAsset();
      if (!asset || !this.adjustModal) return;
      this.adjustTargetId = sel?.instanceId || this.getSelectedItems()[0]?.instanceId;
      const target = this.items.find((i) => i.instanceId === this.adjustTargetId);
      this.adjustSnapshot = {
        hue: target?.hue ?? asset.hue ?? 0,
        saturation: target?.saturation ?? asset.saturation ?? 100,
        brightness: target?.brightness ?? asset.brightness ?? 100,
      };
      this.adjustUndoStack = [{ ...this.adjustSnapshot }];
      if (this.hueInput) this.hueInput.value = this.adjustSnapshot.hue;
      if (this.satInput) this.satInput.value = this.adjustSnapshot.saturation;
      if (this.brightInput) this.brightInput.value = this.adjustSnapshot.brightness;
      if (this.adjustUndoBtn) this.adjustUndoBtn.disabled = true;
      this.previewAdjust();
      this.adjustModal.hidden = false;
    }

    pushAdjustPreview() {
      if (!this.adjustUndoStack) this.adjustUndoStack = [];
      const snap = {
        hue: Number(this.hueInput?.value || 0),
        saturation: Number(this.satInput?.value || 100),
        brightness: Number(this.brightInput?.value || 100),
      };
      const last = this.adjustUndoStack[this.adjustUndoStack.length - 1];
      if (last && last.hue === snap.hue && last.saturation === snap.saturation && last.brightness === snap.brightness) return;
      this.adjustUndoStack.push(snap);
      if (this.adjustUndoBtn) this.adjustUndoBtn.disabled = this.adjustUndoStack.length <= 1;
    }

    undoAdjustPreview() {
      if (!this.adjustUndoStack || this.adjustUndoStack.length <= 1) return;
      this.adjustUndoStack.pop();
      const snap = this.adjustUndoStack[this.adjustUndoStack.length - 1];
      if (this.hueInput) this.hueInput.value = snap.hue;
      if (this.satInput) this.satInput.value = snap.saturation;
      if (this.brightInput) this.brightInput.value = snap.brightness;
      if (this.adjustUndoBtn) this.adjustUndoBtn.disabled = this.adjustUndoStack.length <= 1;
      this.previewAdjust();
    }

    resetAdjustPreview() {
      if (!this.adjustSnapshot) return;
      if (this.hueInput) this.hueInput.value = this.adjustSnapshot.hue;
      if (this.satInput) this.satInput.value = this.adjustSnapshot.saturation;
      if (this.brightInput) this.brightInput.value = this.adjustSnapshot.brightness;
      this.adjustUndoStack = [{ ...this.adjustSnapshot }];
      if (this.adjustUndoBtn) this.adjustUndoBtn.disabled = true;
      this.previewAdjust();
    }

    async previewAdjust() {
      const target = this.items.find((i) => i.instanceId === this.adjustTargetId) || this.getSelectedItems()[0];
      const asset = target ? this.assets.get(target.id) : this.getPrimaryAsset();
      if (!asset || !this.adjustPreview) return;
      const hue = Number(this.hueInput?.value || 0);
      const sat = Number(this.satInput?.value || 100);
      const bright = Number(this.brightInput?.value || 100);
      const src = target?.customSource || this.sourceCache.get(asset.id) || asset.sourceUrl || asset.imageUrl;
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
      const base = document.createElement('canvas');
      base.width = img.naturalWidth;
      base.height = img.naturalHeight;
      base.getContext('2d').drawImage(img, 0, 0);
      const out = GB().applyColorAdjust(base, { hue, saturation: sat, brightness: bright });
      this.adjustPreview.innerHTML = '';
      this.adjustPreview.appendChild(out);
    }

    async applyAdjust(applyToAll = false) {
      const target = this.items.find((i) => i.instanceId === this.adjustTargetId) || this.getSelectedItems()[0];
      if (!target) return;
      const values = {
        hue: Number(this.hueInput?.value || 0),
        saturation: Number(this.satInput?.value || 100),
        brightness: Number(this.brightInput?.value || 100),
      };
      const targets = applyToAll
        ? this.items.filter((i) => i.id === target.id)
        : [target];
      targets.forEach((item) => {
        item.hue = values.hue;
        item.saturation = values.saturation;
        item.brightness = values.brightness;
        this.clearItemImageCache(item.instanceId);
      });
      await Promise.all(targets.map((item) => this.refreshItemImage(item)));
      this.closeAdjustModal();
      this.renderAll();
      this.pushHistory();
    }

    async openBgModal() {
      const sel = this.getSelectedItems().find((i) => !i.kind || i.kind === 'image') || this.getSelectedItems()[0];
      const asset = sel ? this.assets.get(sel.id) : this.getPrimaryAsset();
      if (!asset || !this.bgModal) return;
      const src = sel?.customSource || this.sourceCache.get(asset.id) || asset.sourceUrl || asset.imageUrl;
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      this.bgEdit = {
        assetId: asset.id,
        targetInstanceId: sel?.instanceId,
        history: [GB().cloneCanvas(canvas)],
        historyIndex: 0,
        sourceCanvas: GB().cloneCanvas(canvas),
      };
      if (this.bgWhiteInput) this.bgWhiteInput.value = 248;
      if (this.bgBlackInput) this.bgBlackInput.value = 0;
      if (this.bgFeatherInput) this.bgFeatherInput.value = 8;
      if (this.bgTolInput) this.bgTolInput.value = 48;
      if (this.bgUndoBtn) this.bgUndoBtn.disabled = true;
      this.setBgTab('auto');
      this.runBgAutoPreview();
      this.initBgEraserStage();
      this.bgModal.hidden = false;
    }

    closeBgModal() {
      if (this.bgEdit?.eraserStage) this.bgEdit.eraserStage.destroy();
      this.bgEdit = null;
      if (this.bgEraserStage) this.bgEraserStage.innerHTML = '';
      if (this.bgModal) this.bgModal.hidden = true;
    }

    currentBgCanvas() {
      if (!this.bgEdit) return null;
      return this.bgEdit.history[this.bgEdit.historyIndex];
    }

    pushBgHistory(canvas) {
      this.bgEdit.history = this.bgEdit.history.slice(0, this.bgEdit.historyIndex + 1);
      this.bgEdit.history.push(GB().cloneCanvas(canvas));
      if (this.bgEdit.history.length > 30) this.bgEdit.history.shift();
      this.bgEdit.historyIndex = this.bgEdit.history.length - 1;
      if (this.bgUndoBtn) this.bgUndoBtn.disabled = this.bgEdit.historyIndex <= 0;
      this.renderBgPreview();
      this.refreshBgEraserImage();
    }

    undoBgEdit() {
      if (!this.bgEdit || this.bgEdit.historyIndex <= 0) return;
      this.bgEdit.historyIndex -= 1;
      if (this.bgUndoBtn) this.bgUndoBtn.disabled = this.bgEdit.historyIndex <= 0;
      this.renderBgPreview();
      this.refreshBgEraserImage();
    }

    resetBgEdit() {
      if (!this.bgEdit) return;
      this.bgEdit.history = [GB().cloneCanvas(this.bgEdit.sourceCanvas)];
      this.bgEdit.historyIndex = 0;
      if (this.bgUndoBtn) this.bgUndoBtn.disabled = true;
      this.renderBgPreview();
      this.refreshBgEraserImage();
    }

    setBgTab(tab) {
      this.root.querySelectorAll('[data-pgb-bg-tab]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.pgbBgTab === tab);
      });
      this.root.querySelectorAll('[data-pgb-bg-panel]').forEach((panel) => {
        const on = panel.dataset.pgbBgPanel === tab;
        panel.hidden = !on;
        panel.classList.toggle('is-active', on);
      });
      if (this.bgPreview) this.bgPreview.hidden = tab === 'eraser';
      if (tab === 'eraser') this.initBgEraserStage();
    }

    renderBgPreview() {
      const canvas = this.currentBgCanvas();
      if (!canvas || !this.bgPreview) return;
      const preview = GB().cloneCanvas(canvas);
      const maxW = this.bgPreview.clientWidth || 640;
      const scale = Math.min(1, maxW / preview.width);
      if (scale < 1) {
        const scaled = document.createElement('canvas');
        scaled.width = Math.round(preview.width * scale);
        scaled.height = Math.round(preview.height * scale);
        scaled.getContext('2d').drawImage(preview, 0, 0, scaled.width, scaled.height);
        this.bgPreview.innerHTML = '';
        this.bgPreview.appendChild(scaled);
      } else {
        this.bgPreview.innerHTML = '';
        this.bgPreview.appendChild(preview);
      }
    }

    runBgAutoPreview() {
      if (!this.bgEdit) return;
      const base = GB().cloneCanvas(this.bgEdit.sourceCanvas);
      const num = (input, fallback) => {
        const v = Number(input?.value);
        return Number.isFinite(v) && input?.value !== '' ? v : fallback;
      };
      GB().removeAnyBackground(base, {
        tolerance: num(this.bgTolInput, 48),
        feather: num(this.bgFeatherInput, 8) > 0 ? 1 : 0,
      });
      const whiteTol = num(this.bgWhiteInput, 0);
      const blackTol = num(this.bgBlackInput, 0);
      if (whiteTol > 0 || blackTol > 0) {
        GB().removeBackgroundAdvanced(base, {
          mode: 'legacy',
          whiteTol,
          blackTol,
          feather: num(this.bgFeatherInput, 8),
        });
      }
      this.bgEdit.history = [base];
      this.bgEdit.historyIndex = 0;
      if (this.bgUndoBtn) this.bgUndoBtn.disabled = true;
      this.renderBgPreview();
      this.refreshBgEraserImage();
    }

    applyBgColorRemove() {
      const canvas = this.currentBgCanvas();
      if (!canvas || !this.bgPickColour) return;
      const hex = this.bgPickColour.value;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const out = GB().cloneCanvas(canvas);
      GB().removeColorFromCanvas(out, {
        r, g, b,
        tolerance: Number(this.bgColTol?.value || 32),
        feather: Number(this.bgFeatherInput?.value || 8),
      });
      this.pushBgHistory(out);
    }

    initBgEraserStage() {
      if (!this.bgEraserStage || !this.bgEdit) return;
      this.bgEraserStage.innerHTML = '';
      const canvas = this.currentBgCanvas();
      const maxW = Math.max(this.bgEraserStage.clientWidth || 640, 480);
      const maxH = Math.max(this.bgEraserStage.clientHeight || 480, 360);
      const scale = Math.min(1, maxW / canvas.width, maxH / canvas.height);
      const dispW = Math.round(canvas.width * scale);
      const dispH = Math.round(canvas.height * scale);
      const stage = new Konva.Stage({ container: this.bgEraserStage, width: dispW, height: dispH });
      const layer = new Konva.Layer();
      const kImg = new Konva.Image({ x: 0, y: 0, width: dispW, height: dispH });
      const img = new Image();
      img.onload = () => { kImg.image(img); layer.batchDraw(); };
      img.src = canvas.toDataURL('image/png');
      layer.add(kImg);
      stage.add(layer);

      const cursorLayer = new Konva.Layer({ listening: false });
      const outerRing = new Konva.Circle({
        stroke: '#ed168c', strokeWidth: 1.5, fill: 'rgba(255,255,255,0.06)',
      });
      const innerRing = new Konva.Circle({
        stroke: 'rgba(17,17,17,0.55)', strokeWidth: 1, dash: [4, 4],
      });
      cursorLayer.add(outerRing);
      cursorLayer.add(innerRing);
      cursorLayer.hide();
      stage.add(cursorLayer);

      const brushRadius = () => (Number(this.eraserSizeInput?.value || 40) * scale) / 2;
      const hardRadius = () => brushRadius() * Math.max(0.12, Number(this.eraserHardInput?.value || 85) / 100);

      const updateCursor = (pos) => {
        if (!pos) {
          cursorLayer.hide();
          return;
        }
        cursorLayer.show();
        outerRing.position(pos);
        innerRing.position(pos);
        outerRing.radius(brushRadius());
        innerRing.radius(hardRadius());
        cursorLayer.batchDraw();
      };

      let painting = false;
      let last = null;
      let sizing = false;
      let sizeStart = 0;
      let sizeStartVal = 40;

      const paintAt = (pos) => {
        if (!pos) return;
        const x = (pos.x / dispW) * canvas.width;
        const y = (pos.y / dispH) * canvas.height;
        const size = Number(this.eraserSizeInput?.value || 40) * (canvas.width / dispW);
        const hard = Number(this.eraserHardInput?.value || 85);
        if (last) GB().applyEraserStroke(canvas, last.x, last.y, x, y, size, hard);
        else GB().applyEraserDot(canvas.getContext('2d'), x, y, size, hard);
        last = { x, y };
        img.src = canvas.toDataURL('image/png');
        updateCursor(pos);
      };

      stage.on('mousedown touchstart', (e) => {
        const evt = e.evt;
        if (evt.button === 2 && evt.altKey) {
          sizing = true;
          sizeStart = evt.clientY;
          sizeStartVal = Number(this.eraserSizeInput?.value || 40);
          evt.preventDefault();
          return;
        }
        painting = true;
        last = null;
        paintAt(stage.getPointerPosition());
      });
      stage.on('mousemove touchmove', (e) => {
        const evt = e.evt;
        const pos = stage.getPointerPosition();
        updateCursor(pos);
        if (sizing) {
          const delta = sizeStart - evt.clientY;
          const next = Math.max(4, Math.min(400, Math.round(sizeStartVal + delta * 0.5)));
          if (this.eraserSizeInput) this.eraserSizeInput.value = next;
          updateCursor(pos);
          return;
        }
        if (!painting) return;
        paintAt(pos);
      });
      stage.on('mouseup touchend mouseleave', () => {
        if (sizing) { sizing = false; return; }
        if (painting) {
          painting = false;
          last = null;
          this.pushBgHistory(canvas);
        }
      });
      stage.on('contextmenu', (e) => e.evt.preventDefault());
      this.bgEdit.eraserStage = stage;
      this.bgEdit.eraserScale = scale;
    }

    refreshBgEraserImage() {
      if (!this.bgEdit?.eraserStage) return;
      const canvas = this.currentBgCanvas();
      const layer = this.bgEdit.eraserStage.findOne('Image');
      if (!layer) return;
      const img = new Image();
      img.onload = () => { layer.image(img); layer.getLayer()?.batchDraw(); };
      img.src = canvas.toDataURL('image/png');
    }

    async applyBgEdit() {
      const asset = this.assets.get(this.bgEdit?.assetId);
      const canvas = this.currentBgCanvas();
      const target = this.items.find((i) => i.instanceId === this.bgEdit?.targetInstanceId)
        || this.getSelectedItems().find((i) => !i.kind || i.kind === 'image');
      if (!asset || !canvas || !target) return;
      const trimmed = GB().trimTransparentCanvas(await GB().canvasToImage(canvas));
      const final = await GB().canvasToImage(trimmed.canvas);
      target.customSource = final.src;
      const widthMm = GB().roundMm(GB().pxToMm(final.naturalWidth, this.config.defaultDpi));
      target.widthMm = Math.min(widthMm, this.config.sheetWidthMm);
      target.heightMm = GB().roundMm((target.widthMm / final.naturalWidth) * final.naturalHeight);
      Object.assign(target, this.clampItemToSheet(target));
      this.clearItemImageCache(target.instanceId);
      await this.refreshItemImage(target);
      this.closeBgModal();
      this.pushHistory();
      this.renderAll();
    }

    closeCropModal() {
      if (this.cropStage?.stage) this.cropStage.stage.destroy();
      this.cropStage = null;
      if (this.cropStageHost) this.cropStageHost.innerHTML = '';
      if (this.cropModal) this.cropModal.hidden = true;
    }

    openCropModal() {
      const asset = this.getPrimaryAsset();
      if (!asset || !this.cropModal || !this.cropStageHost) return;
      const img = this.imageCache.get(asset.id);
      if (!img) return;

      this.cropAssetId = asset.id;
      this.cropModal.hidden = false;
      this.cropStageHost.innerHTML = '';

      const pw = asset.pixelWidth || img.naturalWidth;
      const ph = asset.pixelHeight || img.naturalHeight;
      const maxW = Math.min(this.cropStageHost.clientWidth || 560, 640);
      const scale = Math.min(1, maxW / pw);
      const dispW = Math.round(pw * scale);
      const dispH = Math.round(ph * scale);

      const stage = new Konva.Stage({
        container: this.cropStageHost,
        width: dispW,
        height: dispH,
      });
      const layer = new Konva.Layer();
      layer.add(new Konva.Image({
        image: img,
        x: 0,
        y: 0,
        width: dispW,
        height: dispH,
        listening: false,
      }));

      const cropRect = new Konva.Rect({
        x: 0,
        y: 0,
        width: dispW,
        height: dispH,
        stroke: '#ed168c',
        strokeWidth: 2,
        dash: [8, 4],
        draggable: true,
        fill: 'rgba(255,255,255,0.06)',
      });

      cropRect.dragBoundFunc((pos) => {
        const w = cropRect.width() * cropRect.scaleX();
        const h = cropRect.height() * cropRect.scaleY();
        return {
          x: Math.max(0, Math.min(pos.x, dispW - w)),
          y: Math.max(0, Math.min(pos.y, dispH - h)),
        };
      });

      const tr = new Konva.Transformer({
        nodes: [cropRect],
        keepRatio: false,
        rotateEnabled: false,
        borderStroke: '#ed168c',
        anchorStroke: '#ed168c',
        enabledAnchors: [
          'top-left', 'top-right', 'bottom-left', 'bottom-right',
          'middle-left', 'middle-right', 'top-center', 'bottom-center',
        ],
        boundBoxFunc: (oldBox, newBox) => {
          if (newBox.width < 8 || newBox.height < 8) return oldBox;
          const x = Math.max(0, newBox.x);
          const y = Math.max(0, newBox.y);
          const width = Math.min(newBox.width, dispW - x);
          const height = Math.min(newBox.height, dispH - y);
          return { ...newBox, x, y, width, height };
        },
      });

      tr.on('transformend', () => {
        const w = Math.max(8, cropRect.width() * cropRect.scaleX());
        const h = Math.max(8, cropRect.height() * cropRect.scaleY());
        let x = cropRect.x();
        let y = cropRect.y();
        x = Math.max(0, Math.min(x, dispW - w));
        y = Math.max(0, Math.min(y, dispH - h));
        cropRect.position({ x, y });
        cropRect.width(w);
        cropRect.height(h);
        cropRect.scaleX(1);
        cropRect.scaleY(1);
        layer.batchDraw();
      });

      layer.add(cropRect);
      layer.add(tr);
      stage.add(layer);
      this.cropStage = { stage, cropRect, scale, pw, ph, tr };
    }

    normalizeCropRect() {
      if (!this.cropStage?.cropRect) return null;
      const { cropRect, scale, pw, ph } = this.cropStage;
      if (this.cropStage.tr) this.cropStage.tr.nodes([]);
      const sw = cropRect.width() * cropRect.scaleX();
      const sh = cropRect.height() * cropRect.scaleY();
      cropRect.width(sw);
      cropRect.height(sh);
      cropRect.scaleX(1);
      cropRect.scaleY(1);
      const sx = cropRect.x();
      const sy = cropRect.y();
      const x = Math.max(0, Math.min(pw - 1, Math.round(sx / scale)));
      const y = Math.max(0, Math.min(ph - 1, Math.round(sy / scale)));
      const w = Math.max(1, Math.min(pw - x, Math.round(sw / scale)));
      const h = Math.max(1, Math.min(ph - y, Math.round(sh / scale)));
      return { x, y, w, h };
    }

    async applyCrop() {
      const asset = this.assets.get(this.cropAssetId);
      if (!asset || !this.cropStage) return;
      const crop = this.normalizeCropRect();
      if (!crop) return;

      const src = this.sourceCache.get(asset.id) || asset.sourceUrl || asset.imageUrl;
      const img = new Image();
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = () => rej(new Error('Could not load image for crop'));
        img.src = src;
      });

      const canvas = document.createElement('canvas');
      canvas.width = crop.w;
      canvas.height = crop.h;
      canvas.getContext('2d').drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
      const final = await GB().canvasToImage(canvas);

      asset.sourceUrl = final.src;
      asset.imageUrl = final.src;
      asset.pixelWidth = final.naturalWidth;
      asset.pixelHeight = final.naturalHeight;
      const widthMm = GB().roundMm(GB().pxToMm(final.naturalWidth, this.config.defaultDpi));
      asset.widthMm = Math.min(widthMm, this.config.sheetWidthMm - (this.config.safeMarginMm || 0) * 2);
      asset.heightMm = GB().roundMm((asset.widthMm / final.naturalWidth) * final.naturalHeight);
      this.sourceCache.set(asset.id, final.src);
      this.imageCache.set(asset.id, final);

      this.items.filter((i) => i.id === asset.id).forEach((item) => {
        item.widthMm = asset.widthMm;
        item.heightMm = asset.heightMm;
      });

      if (this.cropStage.stage) this.cropStage.stage.destroy();
      this.cropStage = null;
      if (this.cropModal) this.cropModal.hidden = true;
      this.items.filter((i) => i.id === asset.id).forEach((item) => Object.assign(item, this.clampItemToSheet(item)));
      this.pushHistory();
      this.renderAll();
    }

    downloadExport() {
      const dpi = GB().PRODUCTION_DPI;
      const w = Math.round(GB().mmToPx(this.config.sheetWidthMm, dpi));
      const h = Math.round(GB().mmToPx(this.sheetHeightMm(), dpi));
      const canvas = this.renderSheetToCanvas(w, h);
      const stamp = Date.now();
      const png = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.download = `printora-gangsheet-${Math.round(this.sheetHeightMm())}mm-300dpi-${stamp}.png`;
      a.href = png;
      a.click();
      try {
        const projects = JSON.parse(localStorage.getItem('printora_gang_exports') || '[]');
        projects.unshift({
          id: stamp,
          preview: this.renderSheetToCanvas(240, Math.round(240 * h / w)).toDataURL('image/jpeg', 0.6),
          widthMm: this.config.sheetWidthMm,
          heightMm: this.sheetHeightMm(),
          designCount: this.items.length,
          price: GB().priceGangSheet(this.billableLengthMm(), this.config).price,
          createdAt: new Date().toISOString(),
        });
        localStorage.setItem('printora_gang_exports', JSON.stringify(projects.slice(0, 12)));
      } catch (_) {}
    }

    /* Paint the whole sheet (transparent background) into a canvas of the given pixel size. */
    renderSheetToCanvas(w, h) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w));
      canvas.height = Math.max(1, Math.round(h));
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = canvas.width / this.config.sheetWidthMm;
      const dpi = (scale * 25.4);
      this.items.forEach((item) => {
        const x = item.xMm * scale;
        const y = item.yMm * scale;
        const iw = item.widthMm * scale;
        const ih = item.heightMm * scale;
        ctx.save();
        /* Local space: origin at the item's top-left, rotation about its centre (matches the canvas) */
        const rot = ((item.rotationDeg || 0) * Math.PI) / 180;
        ctx.translate(x + iw / 2, y + ih / 2);
        if (rot) ctx.rotate(rot);
        ctx.translate(-iw / 2, -ih / 2);

        if (item.maskShapeId === 'circle' || item.maskShapeId === 'ellipse') {
          ctx.beginPath();
          ctx.ellipse(iw / 2, ih / 2, iw / 2, ih / 2, 0, 0, Math.PI * 2);
          ctx.clip();
        } else if (item.maskPath) {
          try {
            const scaled = new Path2D();
            scaled.addPath(new Path2D(item.maskPath), new DOMMatrix([iw / 100, 0, 0, ih / 100, 0, 0]));
            ctx.clip(scaled);
          } catch (_) {
            try {
              ctx.scale(iw / 100, ih / 100);
              ctx.clip(new Path2D(item.maskPath));
              ctx.scale(100 / iw, 100 / ih);
            } catch (__) {}
          }
        }

        if (!item.kind || item.kind === 'image') {
          const img = this.imageCache.get(item.id);
          if (img) {
            if (item.maskShapeId || item.maskPath) {
              const cb = this.maskContentBox(item, iw, ih, scale);
              ctx.drawImage(img, cb.x, cb.y, cb.w, cb.h);
            } else {
              ctx.drawImage(img, 0, 0, iw, ih);
            }
          }
        } else if (item.kind === 'text') {
          const fontPx = Math.max(12, (item.fontSizePx || 48) * (dpi / 96));
          ctx.font = `${/italic/i.test(item.fontStyle || '') ? 'italic ' : ''}${/bold/i.test(item.fontStyle || 'bold') ? 'bold ' : ''}${fontPx}px "${item.fontFamily || 'Montserrat'}"`;
          ctx.fillStyle = item.fill || '#111';
          ctx.textBaseline = 'alphabetic';
          if (item.stroke && Number(item.strokeWidth) > 0) {
            ctx.strokeStyle = item.stroke;
            ctx.lineWidth = Math.max(1, (item.strokeWidth || 1) * (dpi / 96));
            ctx.strokeText(item.text || '', 0, fontPx);
          }
          ctx.fillText(item.text || '', 0, fontPx);
        } else if (item.kind === 'shape' || item.kind === 'icon') {
          ctx.scale(
            item.kind === 'icon' ? iw / 24 : iw / (item.path ? 100 : 1),
            item.kind === 'icon' ? ih / 24 : ih / (item.path ? 100 : 1)
          );
          if (item.path) {
            const p = new Path2D(item.path);
            if (item.fill && item.fill !== 'transparent') {
              ctx.fillStyle = item.fill;
              ctx.fill(p);
            }
            ctx.strokeStyle = item.stroke || '#111';
            ctx.lineWidth = item.strokeWidth || 2;
            ctx.stroke(p);
          } else if (item.shapeId === 'circle' || item.shapeId === 'ellipse') {
            ctx.beginPath();
            ctx.ellipse(50, 50, 50, 50, 0, 0, Math.PI * 2);
            ctx.fillStyle = item.fill || '#ed168c';
            ctx.fill();
          } else {
            ctx.fillStyle = item.fill || '#ed168c';
            ctx.fillRect(0, 0, item.path ? 100 : iw, item.path ? 100 : ih);
          }
        }
        ctx.restore();
      });
      return canvas;
    }

    /* ---------- Saved gang sheets (per customer / device) ---------- */

    projectPayload(name) {
      return {
        format: 'printora-gangsheet',
        version: 1,
        name: name || this.defaultProjectName(),
        savedAt: new Date().toISOString(),
        sheetWidthMm: this.config.sheetWidthMm,
        state: this.getState(),
      };
    }

    defaultProjectName() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `Gang sheet ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    projectPreviewDataUrl() {
      const ratio = this.sheetHeightMm() / this.config.sheetWidthMm;
      let w = 220;
      let h = Math.max(40, Math.round(w * ratio));
      if (h > 900) { h = 900; w = Math.max(24, Math.round(900 / ratio)); }
      try {
        const canvas = this.renderSheetToCanvas(w, h);
        /* light checker so transparent sheets still read as a preview */
        const out = document.createElement('canvas');
        out.width = canvas.width;
        out.height = canvas.height;
        const ctx = out.getContext('2d');
        ctx.fillStyle = '#f1f1f4';
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(canvas, 0, 0);
        return out.toDataURL('image/jpeg', 0.7);
      } catch (_) {
        return '';
      }
    }

    async saveProjectToLibrary() {
      if (!this.items.length) {
        this.showError('Add some designs before saving a gang sheet.');
        return;
      }
      const suggested = this.currentProjectName || this.defaultProjectName();
      const name = (window.prompt('Name this gang sheet', suggested) || '').trim();
      if (!name) return;
      const id = this.currentProjectId || GB().uid('proj');
      const record = {
        id,
        scope: this.storageScope,
        name,
        updatedAt: new Date().toISOString(),
        preview: this.projectPreviewDataUrl(),
        heightMm: this.sheetHeightMm(),
        widthMm: this.config.sheetWidthMm,
        designCount: this.items.length,
        price: GB().priceGangSheet(this.billableLengthMm(), this.config).price,
        payload: this.projectPayload(name),
      };
      try {
        await ProjectStore.put(record);
        this.currentProjectId = id;
        this.currentProjectName = name;
        this.flashSaveStatus('Gang sheet saved ✓');
        this.renderProjectsList();
      } catch (err) {
        this.showError('Could not save this gang sheet on this device (storage full?). Use "Download file" instead.');
      }
    }

    async renderProjectsList() {
      if (!this.projectsList) return;
      let records = [];
      try { records = await ProjectStore.list(this.storageScope); } catch (_) { records = []; }
      if (!records.length) {
        this.projectsList.innerHTML = '<p class="pgb-empty pgb-empty--small">No saved gang sheets yet. Save one to reopen it later on this device/account.</p>';
        return;
      }
      const fmtDate = (iso) => {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      };
      this.projectsList.innerHTML = records.map((r) => `
        <article class="pgb-project${r.id === this.currentProjectId ? ' is-current' : ''}" data-pgb-project-id="${escapeHtml(r.id)}">
          ${r.preview ? `<img src="${escapeHtml(r.preview)}" alt="" loading="lazy">` : '<div class="pgb-project__blank"></div>'}
          <div class="pgb-project__meta">
            <strong title="${escapeHtml(r.name)}">${escapeHtml(r.name)}</strong>
            <span>${r.designCount} design${r.designCount === 1 ? '' : 's'} · ${(r.heightMm / 1000).toFixed(2)} m · ${money(r.price)}</span>
            <span class="pgb-project__date">${escapeHtml(fmtDate(r.updatedAt))}</span>
          </div>
          <div class="pgb-project__actions">
            <button type="button" class="pgb-btn pgb-btn--accent pgb-btn--small" data-pgb-project-load title="Open this gang sheet on the canvas">Open</button>
            <button type="button" class="pgb-icon-btn" data-pgb-project-export title="Download as a file to keep or share">⤓</button>
            <button type="button" class="pgb-icon-btn" data-pgb-project-delete title="Delete this saved gang sheet">✕</button>
          </div>
        </article>`).join('');
    }

    async loadProjectFromLibrary(id) {
      try {
        const rec = await ProjectStore.get(id);
        if (!rec || rec.scope !== this.storageScope) return;
        if (this.items.length && !window.confirm(`Open "${rec.name}"? Your current sheet will be replaced (it stays saved if you saved it).`)) return;
        this.applyProjectPayload(rec.payload, rec.id, rec.name);
      } catch (err) {
        this.showError('Could not open that gang sheet.');
      }
    }

    async deleteProjectFromLibrary(id) {
      try {
        const rec = await ProjectStore.get(id);
        if (!rec || rec.scope !== this.storageScope) return;
        if (!window.confirm(`Delete "${rec.name}"?`)) return;
        await ProjectStore.remove(id);
        if (this.currentProjectId === id) { this.currentProjectId = null; this.currentProjectName = null; }
        this.renderProjectsList();
      } catch (_) {}
    }

    async downloadProjectFile(id = null) {
      let payload;
      let name;
      if (id) {
        const rec = await ProjectStore.get(id).catch(() => null);
        if (!rec) return;
        payload = rec.payload;
        name = rec.name;
      } else {
        if (!this.items.length) { this.showError('Add some designs before downloading a gang sheet file.'); return; }
        name = this.currentProjectName || this.defaultProjectName();
        payload = this.projectPayload(name);
      }
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'gang-sheet'}.printora.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      this.flashSaveStatus('File downloaded ✓');
    }

    loadProjectFile(file) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const payload = JSON.parse(String(reader.result || ''));
          if (payload?.format !== 'printora-gangsheet' || !payload.state) throw new Error('bad');
          if (this.items.length && !window.confirm(`Open "${payload.name || file.name}"? Your current sheet will be replaced.`)) return;
          this.applyProjectPayload(payload, null, payload.name || file.name.replace(/\.printora\.json$/i, ''));
        } catch (_) {
          this.showError('That file is not a Printora gang sheet (.printora.json).');
        }
      };
      reader.onerror = () => this.showError('Could not read that file.');
      reader.readAsText(file);
    }

    applyProjectPayload(payload, projectId, name) {
      this.assets.clear();
      this.imageCache.clear();
      this.sourceCache.clear();
      this.selectedIds.clear();
      this.maskEditId = null;
      this.applyState(payload.state);
      this.currentProjectId = projectId;
      this.currentProjectName = name || null;
      this.history = new HistoryStack();
      this.pushHistory();
      this.setActiveRail('uploads');
      this.renderAll();
      this.renderProjectsList();
      this.flashSaveStatus(`Opened "${name}"`);
      /* Images decode async — repaint once they have loaded */
      const pending = [...this.imageCache.values()].filter((img) => !img.complete);
      pending.forEach((img) => img.addEventListener('load', () => this.renderKonvaItems(), { once: true }));
    }

    flashSaveStatus(text) {
      if (!this.saveStatus) return;
      this.saveStatus.textContent = text;
      clearTimeout(this._saveFlashTimer);
      this._saveFlashTimer = setTimeout(() => this.markSaved(), 2600);
    }

    /* ---------- Renaming ---------- */

    startRename(targetId) {
      if (!this.designList) return;
      const row = this.designList.querySelector(`[data-pgb-asset-id="${CSS.escape(targetId)}"], [data-pgb-instance-id="${CSS.escape(targetId)}"]`);
      const asset = this.assets.get(targetId);
      const item = this.items.find((i) => i.instanceId === targetId);
      const current = asset ? asset.name : (item ? (item.kind === 'text' ? item.text : item.name) : '');
      if (!asset && !item) return;
      const commit = (value) => {
        const name = String(value ?? '').trim();
        if (!name || name === current) { this.renderDesignList(); return; }
        if (asset) {
          asset.name = name;
          this.items.filter((i) => i.id === asset.id).forEach((i) => { i.name = name; });
        } else if (item) {
          item.name = name;
          if (item.kind === 'text') item.text = name;
        }
        this.pushHistory();
        this.renderAll();
      };
      if (!row) {
        commit(window.prompt('Rename', current));
        return;
      }
      const strong = row.querySelector('strong');
      if (!strong) return;
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'pgb-rename-input';
      input.value = current;
      input.maxLength = 80;
      input.setAttribute('aria-label', 'Rename');
      strong.replaceWith(input);
      input.focus();
      input.select();
      let done = false;
      const finish = (save) => {
        if (done) return;
        done = true;
        if (save) commit(input.value);
        else this.renderDesignList();
      };
      input.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') { e.preventDefault(); finish(true); }
        if (e.key === 'Escape') { e.preventDefault(); finish(false); }
      });
      input.addEventListener('blur', () => finish(true));
      input.addEventListener('click', (e) => e.stopPropagation());
    }

    async addToCart() {
      if (!this.form || this.addBtn?.disabled) return;
      this.addBtn.disabled = true;
      try {
        const res = await fetch((window.routes?.cart_add_url) || '/cart/add.js', {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Cart error');
        this.addBtn.textContent = 'Added ✓';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (err) {
        this.showError(err.message);
      } finally {
        setTimeout(() => this.renderStats(), 1200);
      }
    }

    showError(msg) { if (this.errorBox) { this.errorBox.hidden = false; this.errorBox.textContent = msg; } }
    clearError() { if (this.errorBox) { this.errorBox.hidden = true; this.errorBox.textContent = ''; } }

    renderAll(options = {}) {
      /* Zoom is only changed by explicit fit/zoom actions or a viewport resize — never by re-renders */
      if (options.refit) this.applyFitScale();
      this.renderDesignList();
      this.renderKonvaItems();
      this.renderStats(Boolean(options.skipAutoFix));
      this.syncDock();
      this.syncFormatBar();
      this.updateUndoButtons();
    }

    async tryRestoreDraft() {
      try {
        let data = await ProjectStore.getDraft(this.storageScope);
        /* A customer who built a sheet as a guest and then logged in keeps their work */
        if (!data && this.customerId) {
          const guest = await ProjectStore.getDraft('guest');
          if (guest?.state?.items?.length) {
            data = guest;
            await ProjectStore.removeDraft('guest');
          }
        }
        /* Legacy single-key draft from earlier versions */
        if (!data) {
          const legacy = localStorage.getItem('printora_gang_builder_draft');
          if (legacy) {
            try { data = JSON.parse(legacy); } catch (_) { data = null; }
            localStorage.removeItem('printora_gang_builder_draft');
          }
        }
        if (!data) return;
        if (data.state) {
          this.currentProjectId = data.projectId || null;
          this.currentProjectName = data.projectName || null;
          this.applyState(data.state);
          /* Keep the saved layout; only re-arrange if it's actually broken */
          const orphan = [...this.assets.keys()].some((id) => !this.items.some((i) => i.id === id));
          const sheetH = this.sheetHeightMm();
          const broken = GB().hasCollisions(
            this.items, this.config.sheetWidthMm, sheetH, this.config.safeMarginMm, this.spacingMm
          );
          if (orphan) this.reconcileQuantities({ silent: true });
          if (broken) this.nestAll();
          else if (orphan) this.renderAll();
        }
      } catch (_) {
        /* corrupt/unavailable draft — start clean */
      } finally {
        this._restoring = false;
        this.history = new HistoryStack();
        this.pushHistory();
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-pgb-root]').forEach((root) => {
      if (root.dataset.pgbReady) return;
      root.dataset.pgbReady = '1';
      const app = new GangBuilderApp(root);
      app.fitBalancedScale();
      app.renderAll();
      app.tryRestoreDraft().finally(() => {
        app.fitBalancedScale();
        app.renderAll();
        app.renderProjectsList();
        requestAnimationFrame(() => {
          app.fitBalancedScale();
          app.renderKonvaItems();
          app.updateZoomLabel();
          if (app.stageScroll) { app.stageScroll.scrollLeft = 0; app.stageScroll.scrollTop = 0; }
          root.dataset.pgbRestored = '1';
        });
      });
      root.tabIndex = 0;
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
